import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useParams, useNavigate, useLocation } from 'react-router-dom';
import { auth } from './firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import axios from 'axios';
import HomePage from './HomePage';
import OrganiserLogin from './OrganiserLogin';
import OrganiserDashboard from './OrganiserDashboard';
import TournamentDetails from './TournamentDetails';
import TabletLogin from './TabletLogin';
import TabletWaiting from './TabletWaiting';
import ManualMatchSetup from './ManualMatchSetup';
import PrivacyPolicy from './PrivacyPolicy';
import PlayerRegistration from './PlayerRegistration';
import RegistrationSuccess from './RegistrationSuccess';
import AdHocClockSelector from './AdHocClockSelector';
import AdHocClockDisplay from './AdHocClockDisplay';
import AdminDashboard from './AdminDashboard';
import PlayerGrading from './PlayerGrading';
import Book from './Book';

function MatchSession() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check URL for view parameter (view=display or view=control)
  const searchParams = new URLSearchParams(location.search);
  const viewParam = searchParams.get('view');
  const [page, setPage] = useState(viewParam === 'control' ? 'control' : 'display');
  const [duration, setDuration] = useState(45);
  const [remaining, setRemaining] = useState(45);
  const [extensionDuration, setExtensionDuration] = useState(15);
  const [isRunning, setIsRunning] = useState(false);
  const [extensions, setExtensions] = useState(0);
  
  const [p1Name, setP1Name] = useState('Player 1');
  const [p1Frame, setP1Frame] = useState(0);
  const [p1Active, setP1Active] = useState(true);
  const [p1ExtensionUsed, setP1ExtensionUsed] = useState(false);
  const [p1Image, setP1Image] = useState('');
  
  const [p2Name, setP2Name] = useState('Player 2');
  const [p2Frame, setP2Frame] = useState(0);
  const [p2ExtensionUsed, setP2ExtensionUsed] = useState(false);
  const [p2Image, setP2Image] = useState('');
  
  const [matchFormat, setMatchFormat] = useState(5);
  const [tableName, setTableName] = useState('Table 1');
  
  // Run Out tracking for CueScore sync - tracks which frame numbers had ROs
  // e.g., p1RunOutFrames = [2, 4] means Player 1 got ROs for their 2nd and 4th frames
  const [p1RunOutFrames, setP1RunOutFrames] = useState([]);
  const [p2RunOutFrames, setP2RunOutFrames] = useState([]);
  const [pendingFinalRunOut, setPendingFinalRunOut] = useState(null); // {player, matchId, scoreboardCode} - deferred until confirmation

  const [winner, setWinner] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showShotClock, setShowShotClock] = useState(true); // Toggle between shot clock and scoreboard-only view
  const [isEndingMatch, setIsEndingMatch] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);  // NEW: Flag to stop syncing during confirmation
  
  // Lag winner state
  const [lagWinner, setLagWinner] = useState(null); // null, 'player1', 'player2'
  const [lagWinnerConfirmed, setLagWinnerConfirmed] = useState(false);
  const [showLagSelector, setShowLagSelector] = useState(false);
  const [isSettingLagWinner, setIsSettingLagWinner] = useState(false);
  
  const [isOrganiser, setIsOrganiser] = useState(false);
  const [isCueScoreMatch, setIsCueScoreMatch] = useState(false);
  const [storedTournamentId, setStoredTournamentId] = useState(null); // Store for navigation after celebration
  const [isManualMatch, setIsManualMatch] = useState(false); // Track if this is a manual match
  const [scoreboardOnly, setScoreboardOnly] = useState(false); // Track if scoreboard-only mode

  const intervalRef = useRef(null);
  const channelRef = useRef(null);
  const prevScoreRef = useRef({ p1: 0, p2: 0 });
  const audioContextRef = useRef(null);
  const cuescoreUpdateTimeoutRef = useRef(null);
  const isInitializedRef = useRef(false);
  const lastBeepRef = useRef(null);
  const syncDebounceRef = useRef(null);  // NEW: Debounce Firebase syncs
  const isReceivingSyncRef = useRef(false); // NEW: Prevent re-broadcast loop
  const actionPendingRef = useRef(false); // Track meaningful actions vs timer ticks
  const lastWriteRef = useRef(0); // Timestamp of our last Firebase write
  const prevFirebaseDataRef = useRef(null); // Track last data to avoid redundant writes

  // Viewport size tracking for responsive display layout
  const [viewport, setViewport] = useState({ w: window.innerWidth, h: window.visualViewport ? window.visualViewport.height : window.innerHeight });
  useEffect(() => {
    const update = () => setViewport({ w: window.innerWidth, h: window.visualViewport ? window.visualViewport.height : window.innerHeight });
    window.addEventListener('resize', update);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', update);
    }
    return () => {
      window.removeEventListener('resize', update);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', update);
      }
    };
  }, []);
  const isLandscape = viewport.w > viewport.h;
  const isCompact = viewport.h < 700;
  const deviceTier = Math.min(viewport.w, viewport.h) < 500 ? 'phone' : Math.min(viewport.w, viewport.h) < 900 ? 'tablet' : 'large';

  // Check if user is logged in (organiser)
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setIsOrganiser(true);
      } else {
        setIsOrganiser(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Pre-populate match data from tournament if provided
  useEffect(() => {
    if (location.state) {
      const matchData = location.state;
      
      if (matchData.p1Name) setP1Name(matchData.p1Name);
      if (matchData.p2Name) setP2Name(matchData.p2Name);
      if (matchData.matchFormat) setMatchFormat(matchData.matchFormat);
      if (matchData.tournamentName) setTableName(matchData.tournamentName);
      if (matchData.tableName) setTableName(matchData.tableName);
      if (matchData.p1Image) setP1Image(matchData.p1Image);
      if (matchData.p2Image) setP2Image(matchData.p2Image);
      if (matchData.duration) {
        setDuration(matchData.duration);
        setRemaining(matchData.duration);
      }
      if (matchData.extensionDuration) setExtensionDuration(matchData.extensionDuration);
      if (matchData.lagWinner) setLagWinner(matchData.lagWinner);
      if (matchData.lagWinnerConfirmed !== undefined) setLagWinnerConfirmed(matchData.lagWinnerConfirmed);
      
      // Capture tournamentId from location.state for post-celebration navigation
      if (matchData.tournamentId) {
        setStoredTournamentId(matchData.tournamentId);
        console.log('📍 Stored tournamentId from location.state:', matchData.tournamentId);
      }
      
      // Mark as CueScore match if it has a matchId
      if (matchData.matchId) {
        setIsCueScoreMatch(true);
      }
      
      // Capture manual match flag
      if (matchData.manualMatch) {
        setIsManualMatch(true);
        console.log('📍 This is a manual match');
      }
      
      // Capture scoreboard-only mode
      if (matchData.scoreboardOnly) {
        setScoreboardOnly(true);
        setShowShotClock(false); // Start with shot clock hidden
        console.log('📊 Scoreboard-only mode enabled');
      }
      
      console.log('Pre-populated match data:', matchData);
    }
  }, [location.state]);

  // Load saved session state from localStorage on mount
  useEffect(() => {
    const loadSessionData = async () => {
      // First check Firebase for match data (has player names, etc.)
      try {
        const { ref: dbRef, get } = await import('firebase/database');
        const { database } = await import('./firebase');
        
        const sessionRef = dbRef(database, `sessions/${sessionId}`);
        const snapshot = await get(sessionRef);
        
        if (snapshot.exists()) {
          const matchData = snapshot.val();
          console.log('✅ Loading match data from Firebase:', matchData);
          
          // Set as CueScore match
          setIsCueScoreMatch(true);
          setIsOrganiser(true);
          
          // Store tournamentId for navigation after celebration
          if (matchData.tournamentId) {
            setStoredTournamentId(matchData.tournamentId);
            console.log('📍 Stored tournamentId for later navigation:', matchData.tournamentId);
          }
          
          // Load ALL data from Firebase (including scores if they exist)
          if (matchData.p1Name) setP1Name(matchData.p1Name);
          if (matchData.p2Name) setP2Name(matchData.p2Name);
          if (matchData.p1Image) setP1Image(matchData.p1Image);
          if (matchData.p2Image) setP2Image(matchData.p2Image);
          if (matchData.matchFormat) setMatchFormat(matchData.matchFormat);
          if (matchData.tableName) setTableName(matchData.tableName);
          
          // Load scores if they exist in Firebase
          if (matchData.p1Frame !== undefined) setP1Frame(matchData.p1Frame);
          if (matchData.p2Frame !== undefined) setP2Frame(matchData.p2Frame);
          if (matchData.duration !== undefined) setDuration(matchData.duration);
          if (matchData.remaining !== undefined) setRemaining(matchData.remaining);
          if (matchData.isRunning !== undefined) setIsRunning(matchData.isRunning);
          if (matchData.p1Active !== undefined) setP1Active(matchData.p1Active);
          if (matchData.extensions !== undefined) setExtensions(matchData.extensions);
          if (matchData.p1ExtensionUsed !== undefined) setP1ExtensionUsed(matchData.p1ExtensionUsed);
          if (matchData.p2ExtensionUsed !== undefined) setP2ExtensionUsed(matchData.p2ExtensionUsed);
          if (matchData.extensionDuration !== undefined) setExtensionDuration(matchData.extensionDuration);
          if (matchData.winner) setWinner(matchData.winner);
          if (matchData.lagWinner) setLagWinner(matchData.lagWinner);
          if (matchData.lagWinnerConfirmed !== undefined) setLagWinnerConfirmed(matchData.lagWinnerConfirmed);
          
          prevScoreRef.current = { p1: matchData.p1Frame || 0, p2: matchData.p2Frame || 0 };
          
          // Mark as initialized
          isInitializedRef.current = true;
          console.log('✅ Match data loaded from Firebase with scores:', {p1: matchData.p1Frame, p2: matchData.p2Frame});
          return;
        } else {
          console.log('ℹ️ No Firebase session data found for:', sessionId);
        }
      } catch (error) {
        console.log('⚠️ Error loading Firebase match data:', error);
      }
      
      // Fall back to localStorage
      const savedSession = localStorage.getItem(`pool_session_${sessionId}`);
      if (savedSession) {
        try {
          const data = JSON.parse(savedSession);
          console.log('Loading saved session from localStorage:', sessionId);
          
          // Restore all state from localStorage
          setDuration(data.duration || 45);
          setRemaining(data.remaining || 45);
          setExtensionDuration(data.extensionDuration || 15);
          setIsRunning(data.isRunning || false);
          setExtensions(data.extensions || 0);
          setP1Name(data.p1Name || 'Player 1');
          setP1Frame(data.p1Frame || 0);
          setP1Active(data.p1Active !== undefined ? data.p1Active : true);
          setP1ExtensionUsed(data.p1ExtensionUsed || false);
          setP1Image(data.p1Image || '');
          setP2Name(data.p2Name || 'Player 2');
          setP2Frame(data.p2Frame || 0);
          setP2ExtensionUsed(data.p2ExtensionUsed || false);
          setP2Image(data.p2Image || '');
          setMatchFormat(data.matchFormat || 5);
          setTableName(data.tableName || 'Table 1');
          setWinner(data.winner || null);
          setLagWinner(data.lagWinner || null);
          setLagWinnerConfirmed(data.lagWinnerConfirmed || false);
          setShowConfirm(data.showConfirm || false);
          setShowCelebration(data.showCelebration || false);
          if (data.showShotClock !== undefined) setShowShotClock(data.showShotClock);
          
          prevScoreRef.current = { p1: data.p1Frame || 0, p2: data.p2Frame || 0 };
        } catch (error) {
          console.log('Failed to load saved session:', error);
        }
      } else {
        console.log('New session created:', sessionId);
      }
      
      // Mark as initialized after a short delay to ensure state is set
      setTimeout(() => {
        isInitializedRef.current = true;
        console.log('Session initialized, BroadcastChannel sync enabled');
      }, 500);
    };
    
    loadSessionData();
  }, [sessionId]);

  // Listen for tablet unassignment (when organiser removes match from tablet)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const isTablet = searchParams.get('tablet') === 'true';
    const tableId = searchParams.get('tableId');
    
    if (isTablet && tableId) {
      // Listen to this tablet's status
      const setupListener = async () => {
        const { ref: dbRef, onValue } = await import('firebase/database');
        const { database } = await import('./firebase');
        
        const tabletRef = dbRef(database, `tablets/${tableId}/currentMatch`);
        const unsubscribe = onValue(tabletRef, (snapshot) => {
          // If currentMatch is cleared, navigate back to waiting screen
          if (!snapshot.exists() || snapshot.val() === null) {
            console.log('Match unassigned, returning to waiting screen');
            navigate(`/tablet/${tableId}`);
          }
        });
        
        return unsubscribe;
      };
      
      const unsubscribePromise = setupListener();
      
      return () => {
        unsubscribePromise.then(unsubscribe => unsubscribe());
      };
    }
  }, [location.search, navigate]);

  // Screen Wake Lock - Keep screen on during match (display/tablet view)
  useEffect(() => {
    if (page === 'control') return;
    let wakeLock = null;
    let noSleepVideo = null;
    let wakeLockMethod = 'none';
    
    const requestWakeLock = async () => {
      // Method 1: Screen Wake Lock API (iOS 16.4+, modern Android/Chrome)
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await navigator.wakeLock.request('screen');
          // Verify it wasn't silently denied (iOS can do this)
          if (!wakeLock.released) {
            wakeLockMethod = 'api';
            console.log('🔆 Match screen Wake Lock acquired via API');
            wakeLock.addEventListener('release', () => {
              console.log('🔅 Match screen Wake Lock released');
              wakeLockMethod = 'none';
            });
            return;
          } else {
            console.log('⚠️ Wake Lock API returned a released lock — falling back');
          }
        }
      } catch (err) {
        console.log('⚠️ Wake Lock API failed:', err.message, '— falling back');
      }
      
      // Method 2: Non-muted video at near-zero volume.
      // iOS Safari ignores muted video for screen wake purposes, but a non-muted
      // video playing at minimal volume (0.01) keeps the screen awake on both
      // iOS Safari and Android Chrome. This is the same approach used by NoSleep.js.
      try {
        if (!noSleepVideo) {
          noSleepVideo = document.createElement('video');
          noSleepVideo.setAttribute('playsinline', '');
          // NOT muted — iOS only keeps screen awake for non-muted media
          noSleepVideo.setAttribute('loop', '');
          noSleepVideo.volume = 0.01; // Near-zero volume — inaudible but iOS considers it "audible media"
          noSleepVideo.style.position = 'absolute';
          noSleepVideo.style.left = '-9999px';
          noSleepVideo.style.width = '1px';
          noSleepVideo.style.height = '1px';
          noSleepVideo.src = 'data:video/webm;base64,GkXfowEAAAAAAAAfQoaBAUL3gQFC8oEEQvOBCEKChHdlYm1Ch4EEQoWBAhhTgGcBAAAAAAAH4xFNm3RALE27i1OrhBVJqWZTrIHfTbuMU6uEFlSua1OsggEuTbuMU6uEHFO7a1OsggI+7AEAAAAAAACkAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAVSalmAQAAAAAAAEMq17GDD0JATYCNTGF2ZjU4LjI5LjEwMFdBjUxhdmY1OC4yOS4xMDBzpJBhAAAAAAAWVK5rAQAAAAAAADuuAQAAAAAAADLXgQFzxYEBnIEAIrWcg3VuZIaFVl9WUDmDgQEj44OEAmJhAAAAAABiZIEgY6KTT2QAAAAAAAAAM8eBAUSHgQNH';
          document.body.appendChild(noSleepVideo);
        }
        
        const playVideo = () => {
          if (noSleepVideo && wakeLockMethod !== 'video') {
            noSleepVideo.play().then(() => {
              wakeLockMethod = 'video';
              console.log('🔆 NoSleep video playing (non-muted, vol 0.01) — screen will stay on');
            }).catch(e => {
              console.log('⚠️ NoSleep video play failed:', e.message);
            });
          }
        };
        
        // Try immediately
        playVideo();
        
        // Also retry on ANY user interaction (iOS needs gesture for non-muted video)
        const retryOnGesture = () => playVideo();
        document.addEventListener('click', retryOnGesture);
        document.addEventListener('touchstart', retryOnGesture);
        document.addEventListener('touchend', retryOnGesture);
        
        // Store cleanup functions
        noSleepVideo._cleanupListeners = () => {
          document.removeEventListener('click', retryOnGesture);
          document.removeEventListener('touchstart', retryOnGesture);
          document.removeEventListener('touchend', retryOnGesture);
        };
      } catch (err) {
        console.log('⚠️ NoSleep fallback failed:', err.message);
      }
    };
    
    requestWakeLock();
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        requestWakeLock();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wakeLock) {
        wakeLock.release().catch(() => {});
      }
      if (noSleepVideo) {
        if (noSleepVideo._cleanupListeners) {
          noSleepVideo._cleanupListeners();
        }
        noSleepVideo.pause();
        noSleepVideo.remove();
      }
    };
  }, [page]);

  // Initialize Audio Context (iOS-compatible)
  useEffect(() => {
    // Create audio context on first user interaction and unlock iOS audio pipeline
    const initAudio = async () => {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        console.log('🔊 AudioContext created, state:', audioContextRef.current.state);
      }

      // iOS creates AudioContext in 'suspended' state — must resume inside a user gesture
      if (audioContextRef.current.state === 'suspended') {
        try {
          await audioContextRef.current.resume();
          console.log('🔊 AudioContext resumed, state:', audioContextRef.current.state);
        } catch (err) {
          console.log('⚠️ AudioContext resume failed:', err.message);
        }
      }

      // Play a silent buffer to unlock the iOS audio session.
      // iOS Safari blocks programmatic audio unless the very first sound originates
      // from a direct user gesture. A zero-gain oscillator counts as "playing audio"
      // and unlocks the pipeline for all subsequent programmatic playBeep calls.
      try {
        const ctx = audioContextRef.current;
        const silentOsc = ctx.createOscillator();
        const silentGain = ctx.createGain();
        silentGain.gain.setValueAtTime(0, ctx.currentTime);
        silentOsc.connect(silentGain);
        silentGain.connect(ctx.destination);
        silentOsc.start(ctx.currentTime);
        silentOsc.stop(ctx.currentTime + 0.05);
        console.log('🔊 Silent buffer played — iOS audio session unlocked');
      } catch (err) {
        console.log('⚠️ Silent buffer play failed:', err.message);
      }
    };
    
    // Initialize on any click or touch
    document.addEventListener('click', initAudio, { once: true });
    document.addEventListener('touchstart', initAudio, { once: true });

    // iOS Safari suspends AudioContext when the page is backgrounded or screen locks.
    // Re-resume when the page becomes visible again.
    const handleAudioVisibility = async () => {
      if (document.visibilityState === 'visible' && audioContextRef.current) {
        if (audioContextRef.current.state === 'suspended') {
          try {
            await audioContextRef.current.resume();
            console.log('🔊 AudioContext re-resumed after visibility change, state:', audioContextRef.current.state);
          } catch (err) {
            console.log('⚠️ AudioContext re-resume failed:', err.message);
          }
        }
      }
    };
    document.addEventListener('visibilitychange', handleAudioVisibility);
    
    return () => {
      document.removeEventListener('click', initAudio);
      document.removeEventListener('touchstart', initAudio);
      document.removeEventListener('visibilitychange', handleAudioVisibility);
    };
  }, []);

  // Play beep sound (with iOS resume guard)
  const playBeep = async (frequency, duration) => {
    if (!audioContextRef.current) return;

    // iOS may re-suspend the context after inactivity — always check and resume
    if (audioContextRef.current.state === 'suspended') {
      try {
        await audioContextRef.current.resume();
        console.log('🔊 AudioContext resumed before playBeep');
      } catch (err) {
        console.log('⚠️ AudioContext resume before playBeep failed:', err.message);
        return;
      }
    }
    
    try {
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = 'square';
      
      gainNode.gain.setValueAtTime(1.0, audioContextRef.current.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + duration);
      
      oscillator.start(audioContextRef.current.currentTime);
      oscillator.stop(audioContextRef.current.currentTime + duration);
    } catch (error) {
      console.log('Audio play failed:', error);
    }
  };

  // Vibrate device
  const vibrate = (pattern) => {
    if (navigator.vibrate) {
      navigator.vibrate(pattern);
    }
  };

  // Handle countdown alerts
  useEffect(() => {
    const currentSecond = Math.ceil(remaining);
    
    // Prevent duplicate beeps for the same second
    if (currentSecond === lastBeepRef.current) return;
    lastBeepRef.current = currentSecond;
    
    if (currentSecond === 10 || currentSecond === 9 || currentSecond === 8 || currentSecond === 7 || currentSecond === 6) {
      // Normal pitch beep (440Hz)
      playBeep(440, 0.1);
      vibrate(50);
    } else if (currentSecond === 5 || currentSecond === 4 || currentSecond === 3 || currentSecond === 2 || currentSecond === 1) {
      // Higher pitch beep (880Hz)
      playBeep(880, 0.1);
      vibrate(100);
    } else if (currentSecond === 0 && remaining === 0) {
      // Long beep for time expired (220Hz)
      playBeep(220, 2);
      vibrate([200, 100, 200, 100, 200]);
    }
  }, [remaining]);

  // Initialize BroadcastChannel for syncing between tabs (session-specific)
  useEffect(() => {
    try {
      const channelName = `pool_clock_${sessionId}`;
      channelRef.current = new BroadcastChannel(channelName);
      console.log(`Connected to session: ${sessionId}`);
      
      channelRef.current.onmessage = (event) => {
        // Only sync from BroadcastChannel after initial load is complete
        if (!isInitializedRef.current) {
          console.log('Ignoring broadcast during initialization');
          return;
        }
        
        // IMPORTANT: Always accept celebration broadcasts - this closes confirmation on other tabs
        // when organiser confirms the winner
        // But don't re-trigger if we're already celebrating (prevents timer reset)
        if (event.data.showCelebration && !showCelebration) {
          console.log('🎉 Received celebration broadcast - closing confirmation and celebrating!');
          isReceivingSyncRef.current = true;
          setIsConfirming(false);
          setShowConfirm(false);
          setShowCelebration(true);
          setWinner(event.data.winner);
          setTimeout(() => {
            isReceivingSyncRef.current = false;
          }, 0);
          return;
        }
        
        // If already celebrating, ignore all broadcasts (including celebration repeats)
        if (showCelebration) {
          console.log('Ignoring broadcast - already celebrating');
          return;
        }
        
        // Stop syncing during OUR OWN winner confirmation (celebration is handled above)
        if (isConfirming) {
          console.log('Ignoring broadcast - we are confirming winner');
          return;
        }
        
        console.log('Received sync:', event.data);
        // Set flag to prevent re-broadcasting this data
        isReceivingSyncRef.current = true;
        
        // Update all state from the broadcast (but NOT page navigation)
        setDuration(event.data.duration);
        setRemaining(event.data.remaining);
        setExtensionDuration(event.data.extensionDuration || 15);
        setIsRunning(event.data.isRunning);
        setExtensions(event.data.extensions);
        setP1Name(event.data.p1Name);
        setP1Frame(event.data.p1Frame);
        setP1Active(event.data.p1Active);
        setP1ExtensionUsed(event.data.p1ExtensionUsed);
        setP1Image(event.data.p1Image || '');
        setP2Name(event.data.p2Name);
        setP2Frame(event.data.p2Frame);
        setP2ExtensionUsed(event.data.p2ExtensionUsed);
        setP2Image(event.data.p2Image || '');
        setMatchFormat(event.data.matchFormat);
        setTableName(event.data.tableName);
        setWinner(event.data.winner);
        setLagWinner(event.data.lagWinner);
        setLagWinnerConfirmed(event.data.lagWinnerConfirmed);
        // Don't sync showConfirm and showCelebration - each tab manages its own
        
        // Clear flag after state updates are batched (next tick)
        setTimeout(() => {
          isReceivingSyncRef.current = false;
        }, 0);
      };
    } catch (error) {
      console.log('BroadcastChannel not supported');
    }

    return () => {
      if (channelRef.current) channelRef.current.close();
    };
  }, []);

  // Firebase real-time listener for cross-device sync (tablet <-> organiser control panel)
  useEffect(() => {
    let unsubscribe = null;
    
    const setupFirebaseListener = async () => {
      try {
        const { ref: dbRef, onValue } = await import('firebase/database');
        const { database } = await import('./firebase');
        
        const sessionRef = dbRef(database, `sessions/${sessionId}`);
        unsubscribe = onValue(sessionRef, (snapshot) => {
          // Don't sync during initialization
          if (!isInitializedRef.current) {
            return;
          }
          
          if (snapshot.exists()) {
            const data = snapshot.val();
            
            // Ignore our own writes by comparing timestamps
            if (data.lastUpdated && data.lastUpdated === lastWriteRef.current) {
              return;
            }
            
            console.log('📡 Firebase sync received:', { p1Frame: data.p1Frame, p2Frame: data.p2Frame, isRunning: data.isRunning, p1Active: data.p1Active });
            
            isReceivingSyncRef.current = true;
            
            // Always update scores and match state
            if (data.p1Frame !== undefined) setP1Frame(data.p1Frame);
            if (data.p2Frame !== undefined) setP2Frame(data.p2Frame);
            if (data.lagWinner !== undefined) setLagWinner(data.lagWinner);
            if (data.lagWinnerConfirmed !== undefined) setLagWinnerConfirmed(data.lagWinnerConfirmed);
            if (data.extensions !== undefined) setExtensions(data.extensions);
            if (data.p1ExtensionUsed !== undefined) setP1ExtensionUsed(data.p1ExtensionUsed);
            if (data.p2ExtensionUsed !== undefined) setP2ExtensionUsed(data.p2ExtensionUsed);
            
            // Only apply timer/active player state when the sender flagged it as an action
            if (data.isAction) {
              if (data.isRunning !== undefined) setIsRunning(data.isRunning);
              if (data.p1Active !== undefined) setP1Active(data.p1Active);
              if (data.remaining !== undefined) setRemaining(data.remaining);
              console.log('⚡ Applied action:', { isRunning: data.isRunning, p1Active: data.p1Active, remaining: data.remaining });
            }
            
            setTimeout(() => {
              isReceivingSyncRef.current = false;
            }, 600);
          }
        });
      } catch (error) {
        console.log('⚠️ Firebase listener setup failed:', error);
      }
    };
    
    setupFirebaseListener();
    
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [sessionId]);

  // Broadcast state changes to other tabs (but NOT page navigation)
  useEffect(() => {
    // Don't save during initialization - wait until initialized
    if (!isInitializedRef.current) {
      console.log('Skipping save during initialization');
      return;
    }
    
    // CRITICAL: Don't re-broadcast data we just received from another tab
    if (isReceivingSyncRef.current) {
      console.log('Skipping save - received from another tab');
      return;
    }
    
    // CRITICAL: Stop syncing during winner confirmation to prevent infinite loop flickering
    if (isConfirming) {
      console.log('Skipping save during winner confirmation');
      return;
    }
    
    // Save to localStorage
    const sessionData = {
      duration,
      remaining,
      extensionDuration,
      isRunning,
      extensions,
      p1Name,
      p1Frame,
      p1Active,
      p1ExtensionUsed,
      p1Image,
      p2Name,
      p2Frame,
      p2ExtensionUsed,
      p2Image,
      matchFormat,
      tableName,
      winner,
      showConfirm,
      showCelebration,
      showShotClock,
      lagWinner,
      lagWinnerConfirmed,
      timestamp: Date.now()
    };
    
    console.log('Saving session:', { p1Frame, p2Frame, p1Name, p2Name });
    localStorage.setItem(`pool_session_${sessionId}`, JSON.stringify(sessionData));
    
    // Also save to Firebase with DEBOUNCING (limit to 1 sync per 500ms)
    const isAction = actionPendingRef.current;
    actionPendingRef.current = false;
    
    // Build the Firebase payload
    const firebaseData = {
      p1Frame,
      p2Frame,
      duration,
      extensionDuration,
      extensions,
      p1ExtensionUsed,
      p2ExtensionUsed,
      winner,
      lagWinner,
      lagWinnerConfirmed,
    };
    
    // Only sync timer state on meaningful actions (not every tick)
    if (isAction) {
      firebaseData.remaining = remaining;
      firebaseData.isRunning = isRunning;
      firebaseData.p1Active = p1Active;
      firebaseData.isAction = true;
    } else {
      firebaseData.isAction = false;
    }
    
    // Skip Firebase write if nothing changed from last write (prevents loops)
    const dataKey = JSON.stringify(firebaseData);
    if (dataKey === prevFirebaseDataRef.current && !isAction) {
      return;
    }
    prevFirebaseDataRef.current = dataKey;
    
    const saveToFirebase = async () => {
      // Double-check flag before writing (in case we received sync during debounce)
      if (isReceivingSyncRef.current) {
        console.log('⏸️ Skipping Firebase write - received sync during debounce');
        return;
      }
      
      try {
        const { ref: dbRef, update } = await import('firebase/database');
        const { database } = await import('./firebase');
        
        const sessionRef = dbRef(database, `sessions/${sessionId}`);
        const writeTimestamp = Date.now();
        lastWriteRef.current = writeTimestamp;
        
        await update(sessionRef, {
          ...firebaseData,
          lastUpdated: writeTimestamp,
        });
        console.log('✅ Synced to Firebase:', { p1Frame, p2Frame, isAction });
      } catch (error) {
        console.log('⚠️ Firebase sync failed (non-critical):', error);
      }
    };
    
    // Clear existing debounce timeout
    if (syncDebounceRef.current) {
      clearTimeout(syncDebounceRef.current);
    }
    
    // Debounce Firebase syncs - shorter delay for actions, longer for routine saves
    syncDebounceRef.current = setTimeout(() => {
      saveToFirebase();
    }, isAction ? 100 : 500);
    
    // Broadcast to other tabs
    if (channelRef.current) {
      try {
        channelRef.current.postMessage(sessionData);
      } catch (error) {
        // Silently fail if broadcast not available
      }
    }
    
    // Cleanup debounce timeout
    return () => {
      if (syncDebounceRef.current) {
        clearTimeout(syncDebounceRef.current);
      }
    };
  }, [sessionId, duration, remaining, extensionDuration, isRunning, extensions, p1Name, p1Frame, p1Active, p1ExtensionUsed, p1Image, p2Name, p2Frame, p2ExtensionUsed, p2Image, matchFormat, tableName, winner, showConfirm, showCelebration, isConfirming, lagWinner, lagWinnerConfirmed]);

  // Reset extensions and timer when score changes (but don't auto-start)
  useEffect(() => {
    if (prevScoreRef.current.p1 !== p1Frame || prevScoreRef.current.p2 !== p2Frame) {
      console.log('Score changed, resetting extensions and timer');
      actionPendingRef.current = true;
      setP1ExtensionUsed(false);
      setP2ExtensionUsed(false);
      // Reset timer to default duration but DON'T auto-start (racking takes time)
      setRemaining(duration);
      setIsRunning(false);
      prevScoreRef.current = { p1: p1Frame, p2: p2Frame };
    }
  }, [p1Frame, p2Frame, duration]);

  // Sync active player indicator with current breaker when frames change OR lag winner confirmed
  // Consolidated into single useEffect to prevent double-firing and flashing
  useEffect(() => {
    if (lagWinner && lagWinnerConfirmed) {
      const totalFrames = p1Frame + p2Frame;
      // Even total frames = lag winner breaks, odd = other player breaks
      const shouldP1BeActive = totalFrames % 2 === 0 
        ? lagWinner === 'player1' 
        : lagWinner !== 'player1';
      
      if (p1Active !== shouldP1BeActive) {
        console.log(`🔄 Syncing active player: ${shouldP1BeActive ? 'Player 1' : 'Player 2'} (frame ${totalFrames + 1})`);
        actionPendingRef.current = true;
        setP1Active(shouldP1BeActive);
      }
    }
  }, [p1Frame, p2Frame, lagWinner, lagWinnerConfirmed]);

  // Auto-hide celebration after 1.5 seconds for BOTH tablet and control panel
  useEffect(() => {
    console.log('🎊 Celebration effect triggered. showCelebration:', showCelebration, 'page:', page);
    if (showCelebration) {
      console.log('✅ Starting 1.5-second celebration timer...');
      const timer = setTimeout(async () => {
        console.log('⏰ 1.5 seconds passed, hiding celebration...');
        setShowCelebration(false);
        
        // Navigate based on context
        const searchParams = new URLSearchParams(location.search);
        const isTablet = searchParams.get('tablet') === 'true';
        const tableId = searchParams.get('tableId');
        
        console.log('🔍 Navigation check - isTablet:', isTablet, 'tableId:', tableId, 'isOrganiser:', isOrganiser, 'URL:', location.search);
        
        if (isTablet && tableId) {
          // Tablet - navigate back to waiting screen
          console.log('📱 Tablet celebration finished, returning to waiting screen...');
          navigate(`/tablet/${tableId}`);
        } else if (!isTablet && isOrganiser) {
          // Organiser control panel - navigate back to tournament details
          console.log('🎯 Organiser celebration finished, navigating back to tournament...');
          console.log('📍 storedTournamentId:', storedTournamentId);
          
          if (storedTournamentId) {
            console.log('✅ Navigating to tournament:', storedTournamentId);
            navigate(`/tournament/${storedTournamentId}`);
          } else {
            // Fallback: try to fetch from session if storedTournamentId not set
            console.log('⚠️ No storedTournamentId - trying fallback from session data...');
            try {
              const { ref: dbRef, get } = await import('firebase/database');
              const { database } = await import('./firebase');
              
              const sessionRef = dbRef(database, `sessions/${sessionId}`);
              const sessionSnap = await get(sessionRef);
              
              if (sessionSnap.exists()) {
                const sessionData = sessionSnap.val();
                const { tournamentId } = sessionData;
                console.log('📖 Session data retrieved, tournamentId:', tournamentId);
                
                if (tournamentId) {
                  console.log('✅ Fallback: navigating to tournament:', tournamentId);
                  navigate(`/tournament/${tournamentId}`);
                } else {
                  console.log('❌ No tournamentId in session data, navigating to dashboard');
                  navigate('/organiser-dashboard');
                }
              } else {
                console.log('❌ Session not found in Firebase, navigating to dashboard');
                navigate('/organiser-dashboard');
              }
            } catch (error) {
              console.error('❌ Failed to fetch session data:', error);
              navigate('/organiser-dashboard');
            }
          }
        } else {
          console.log('⚠️ No navigation path matched - staying on current page');
        }
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [showCelebration, location.search, isOrganiser, sessionId, navigate, storedTournamentId]);

  // Check for winner
  useEffect(() => {
    if (p1Frame >= matchFormat && matchFormat > 0) {
      console.log(`🏆 WINNER DETECTION: p1Frame=${p1Frame} >= matchFormat=${matchFormat}`);
      console.log(`🏆 Setting winner to 'player1' (${p1Name})`);
      console.log(`🏆 Current state: p1Name=${p1Name}, p2Name=${p2Name}, p1Frame=${p1Frame}, p2Frame=${p2Frame}`);
      setIsConfirming(true);  // NEW: Stop syncing BEFORE showing confirmation
      setShowConfirm(true);
      setWinner('player1');
    } else if (p2Frame >= matchFormat && matchFormat > 0) {
      console.log(`🏆 WINNER DETECTION: p2Frame=${p2Frame} >= matchFormat=${matchFormat}`);
      console.log(`🏆 Setting winner to 'player2' (${p2Name})`);
      console.log(`🏆 Current state: p1Name=${p1Name}, p2Name=${p2Name}, p1Frame=${p1Frame}, p2Frame=${p2Frame}`);
      setIsConfirming(true);  // NEW: Stop syncing BEFORE showing confirmation
      setShowConfirm(true);
      setWinner('player2');
    }
    // Note: p1Name/p2Name intentionally excluded from deps - only used for logging
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p1Frame, p2Frame, matchFormat]);

  // Show lag selector at 0-0 if not already set
  useEffect(() => {
    if (isCueScoreMatch && p1Frame === 0 && p2Frame === 0 && !lagWinnerConfirmed && isInitializedRef.current) {
      setShowLagSelector(true);
    } else {
      setShowLagSelector(false);
    }
  }, [p1Frame, p2Frame, lagWinnerConfirmed, isCueScoreMatch]);

  // Timer countdown
  useEffect(() => {
    if (isRunning && remaining > 0) {
      intervalRef.current = setInterval(() => {
        setRemaining(prev => {
          const newVal = prev - 1;
          if (newVal <= 0) {
            actionPendingRef.current = true;
            setIsRunning(false);
            return 0;
          }
          return newVal;
        });
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isRunning, remaining]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimerColor = () => {
    const currentSecond = Math.ceil(remaining);
    
    if (remaining > 15) return '#22c55e';
    if (remaining > 10) return '#eab308';
    
    // Flash effect for countdown
    if (currentSecond <= 10 && currentSecond > 5) {
      // Orange flash
      return isRunning && Math.floor(remaining) !== remaining ? '#fb923c' : '#ef4444';
    }
    if (currentSecond <= 5 && currentSecond > 0) {
      // Red flash
      return isRunning && Math.floor(remaining) !== remaining ? '#dc2626' : '#991b1b';
    }
    
    return '#ef4444';
  };

  const getTimerStyle = () => {
    const currentSecond = Math.ceil(remaining);
    const isLargePortrait = deviceTier !== 'phone' && !isLandscape;
    const isLargeLandscape = deviceTier !== 'phone' && isLandscape;
    const baseStyle = {
      fontSize: isLargePortrait ? 'clamp(108px, 19.2vh, 194px)' : isLargeLandscape ? 'clamp(110px, 19.8vh, 198px)' : 'clamp(100px, 18vh, 180px)',
      fontWeight: 'bold',
      textAlign: 'center',
      color: getTimerColor(),
      transition: 'all 0.1s ease',
      lineHeight: 1,
      fontFamily: "'JetBrains Mono', monospace",
      letterSpacing: '-0.02em'
    };
    
    // Add shadow flash for extra visibility
    if (currentSecond <= 10 && currentSecond > 0 && isRunning) {
      return {
        ...baseStyle,
        textShadow: `0 0 ${currentSecond <= 5 ? '40px' : '20px'} ${getTimerColor()}`
      };
    }
    
    return baseStyle;
  };

  const handleSwitchPlayer = () => {
    actionPendingRef.current = true;
    setP1Active(!p1Active);
    setRemaining(duration);
    setIsRunning(true);
  };

  const handlePlayerExtension = (player) => {
    actionPendingRef.current = true;
    if (player === 1 && !p1ExtensionUsed) {
      setRemaining(remaining + extensionDuration);
      setP1ExtensionUsed(true);
      setExtensions(extensions + 1);
    } else if (player === 2 && !p2ExtensionUsed) {
      setRemaining(remaining + extensionDuration);
      setP2ExtensionUsed(true);
      setExtensions(extensions + 1);
    }
  };

  // Debounced CueScore score update
  const updateCueScoreScore = async (scoreA, scoreB) => {
    if (!isCueScoreMatch || !sessionId) return;

    try {
      console.log(`Updating CueScore: ${scoreA}-${scoreB}`);
      const functions = getFunctions();
      const updateScore = httpsCallable(functions, 'updateCueScoreScore');
      
      await updateScore({
        sessionId: sessionId,
        scoreA: scoreA,
        scoreB: scoreB,
      });
      
      console.log('✅ CueScore score updated');
    } catch (error) {
      console.error('⚠️ Failed to update CueScore score:', error);
      // Don't alert - scores will still update locally
    }
  };

  // End match in CueScore
  const endCueScoreMatch = async (scoreA, scoreB) => {
    if (!isCueScoreMatch || !sessionId) return;

    try {
      console.log(`Ending CueScore match: ${scoreA}-${scoreB}`);
      const functions = getFunctions();
      const endMatch = httpsCallable(functions, 'endCueScoreMatch');
      
      await endMatch({
        sessionId: sessionId,
        scoreA: scoreA,
        scoreB: scoreB,
      });
      
      console.log('✅ Match ended in CueScore');
    } catch (error) {
      console.error('⚠️ Failed to end match in CueScore:', error);
      // Don't block the celebration
    }
  };

  // Set lag winner in CueScore via WebSocket
  const setCuescoreLagWinner = async (winner, matchId, tableScoreboardCode) => {
    if (!matchId || !tableScoreboardCode) {
      throw new Error('Missing matchId or tableScoreboardCode');
    }

    let iframe = null;
    let socket = null;

    try {
      console.log(`🎯 Setting lag winner in CueScore: ${winner}, matchId: ${matchId}, code: ${tableScoreboardCode}`);

      // Step 1: Load scoreboard iframe to establish session
      await new Promise((resolve, reject) => {
        iframe = document.createElement('iframe');
        iframe.style.cssText = 'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;';
        iframe.src = `https://cuescore.com/scoreboard/?code=${tableScoreboardCode}`;

        const timeout = setTimeout(() => {
          reject(new Error('Scoreboard load timeout'));
        }, 10000);

        iframe.onload = () => {
          clearTimeout(timeout);
          console.log('✅ Scoreboard iframe loaded');
          // Small delay to ensure cookies are set
          setTimeout(resolve, 500);
        };

        iframe.onerror = () => {
          clearTimeout(timeout);
          reject(new Error('Failed to load scoreboard'));
        };

        document.body.appendChild(iframe);
      });

      // Step 2: Connect WebSocket
      await new Promise((resolve, reject) => {
        const wsUrl = `wss://ws.cuescore.com:8443/?matchId=${matchId}&tableCode=${tableScoreboardCode}`;
        console.log(`🔌 Connecting to WebSocket: ${wsUrl}`);

        socket = new WebSocket(wsUrl);

        const timeout = setTimeout(() => {
          reject(new Error('WebSocket connection timeout'));
        }, 10000);

        socket.onopen = () => {
          clearTimeout(timeout);
          console.log('✅ WebSocket connected');
          // Give WebSocket a moment to fully establish before resolving
          setTimeout(resolve, 1000);
        };

        socket.onerror = (error) => {
          clearTimeout(timeout);
          reject(error);
        };
      });

      // Step 3: Send lag winner payload and wait 3 seconds
      await new Promise((resolve) => {
        const currentTime = new Date().toISOString();
        const breaker = winner === 'player1' ? 'A' : 'B';

        // Generate UUID for clientId
        const generateUUID = () => {
          return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
          });
        };

        const payload = {
          action: "ADD",
          clientId: generateUUID(),
          tableCode: tableScoreboardCode,
          matchId: parseInt(matchId, 10),  // MUST be integer
          expectedServerNotes: [],
          newNotes: [
            { note: "frame start", time: currentTime },
            { note: `${breaker} breaking`, time: currentTime }
          ]
        };

        console.log('📤 Sending lag winner payload:', JSON.stringify(payload, null, 2));

        // Set up message handler to log responses (but don't wait for them)
        socket.onmessage = (event) => {
          try {
            console.log('📨 RAW CueScore message:', event.data);
            const data = JSON.parse(event.data);
            console.log('📨 PARSED CueScore response:', JSON.stringify(data, null, 2));
          } catch (error) {
            console.error('❌ Failed to parse CueScore response:', error, 'Raw data:', event.data);
          }
        };
        
        socket.onerror = (error) => {
          console.error('❌ WebSocket error:', error);
        };
        
        socket.onclose = (event) => {
          console.log('🔌 WebSocket closed:', event.code, event.reason);
        };

        // Send the payload
        try {
          socket.send(JSON.stringify(payload));
          console.log('✅ Payload sent to CueScore');
          console.log('⏳ Waiting 3 seconds before proceeding...');
          
          // Wait 3 seconds then auto-proceed
          setTimeout(() => {
            console.log('✅ 3 seconds elapsed - proceeding to match');
            resolve({ success: true });
          }, 3000);
        } catch (error) {
          console.error('❌ Failed to send payload:', error);
          // Still proceed after 3 seconds even if send failed
          setTimeout(() => {
            resolve({ success: true });
          }, 3000);
        }
      });

    } finally {
      // Cleanup
      if (socket) {
        socket.close();
      }
      if (iframe && iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    }
  };

  // Record Run Out in CueScore via WebSocket
  const setCuescoreRunOut = async (player, matchId, tableScoreboardCode) => {
    if (!matchId || !tableScoreboardCode) {
      console.warn('⚠️ Missing matchId or tableScoreboardCode for RO - skipping CueScore sync');
      return;
    }

    let iframe = null;
    let socket = null;

    try {
      const playerLetter = player === 'player1' ? 'A' : 'B';
      console.log(`🎱 Recording Run Out in CueScore: Player ${playerLetter}, matchId: ${matchId}, code: ${tableScoreboardCode}`);

      // Step 1: Load scoreboard iframe to establish session
      await new Promise((resolve, reject) => {
        iframe = document.createElement('iframe');
        iframe.style.cssText = 'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;';
        iframe.src = `https://cuescore.com/scoreboard/?code=${tableScoreboardCode}`;

        const timeout = setTimeout(() => {
          reject(new Error('Scoreboard load timeout'));
        }, 10000);

        iframe.onload = () => {
          clearTimeout(timeout);
          console.log('✅ Scoreboard iframe loaded for RO');
          setTimeout(resolve, 500);
        };

        iframe.onerror = () => {
          clearTimeout(timeout);
          reject(new Error('Failed to load scoreboard'));
        };

        document.body.appendChild(iframe);
      });

      // Step 2: Connect WebSocket and send RO with CONFLICT retry logic
      await new Promise((resolve, reject) => {
        const wsUrl = `wss://ws.cuescore.com:8443/?matchId=${matchId}&tableCode=${tableScoreboardCode}`;
        console.log(`🔌 Connecting to WebSocket for RO: ${wsUrl}`);

        socket = new WebSocket(wsUrl);
        let retryCount = 0;
        const maxRetries = 3;
        let resolved = false;

        const generateUUID = () => {
          return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
          });
        };

        const sendPayload = (expectedNotes = []) => {
          const currentTime = new Date().toISOString();
          const payload = {
            action: "ADD",
            clientId: generateUUID(),
            tableCode: tableScoreboardCode,
            matchId: parseInt(matchId, 10),
            expectedServerNotes: expectedNotes,
            newNotes: [
              { note: `${playerLetter} frame win runout`, time: currentTime }
            ]
          };

          console.log(`📤 Sending Run Out payload (attempt ${retryCount + 1}):`, JSON.stringify(payload, null, 2));
          socket.send(JSON.stringify(payload));
        };

        const timeout = setTimeout(() => {
          if (!resolved) {
            resolved = true;
            console.log('⏱️ RO WebSocket timeout - proceeding anyway');
            resolve({ success: false, reason: 'timeout' });
          }
        }, 10000);

        socket.onopen = () => {
          console.log('✅ WebSocket connected for RO');
          // Send first attempt with empty expectedServerNotes
          // CueScore will return CONFLICT with current serverNotes if notes exist
          setTimeout(() => sendPayload([]), 500);
        };

        socket.onmessage = (event) => {
          try {
            console.log('📨 RAW CueScore RO response:', event.data);
            const data = JSON.parse(event.data);
            console.log('📨 PARSED CueScore RO response:', JSON.stringify(data, null, 2));

            if (data.action === 'CONFLICT' && retryCount < maxRetries) {
              // CONFLICT means our expectedServerNotes didn't match
              // CueScore gives us the current serverNotes - retry with those
              retryCount++;
              console.log(`🔄 CONFLICT detected - retrying with ${data.serverNotes?.length || 0} serverNotes (attempt ${retryCount + 1})`);
              setTimeout(() => sendPayload(data.serverNotes || []), 300);
            } else if (data.action === 'OK' || data.action === 'SUCCESS' || data.serverNotes) {
              // Success! serverNotes in response without CONFLICT means it worked
              if (!resolved) {
                resolved = true;
                clearTimeout(timeout);
                console.log('✅ Run Out synced to CueScore successfully');
                resolve({ success: true });
              }
            } else if (data.action === 'CONFLICT') {
              // Max retries exceeded
              if (!resolved) {
                resolved = true;
                clearTimeout(timeout);
                console.log('❌ Run Out sync failed after max retries');
                resolve({ success: false, reason: 'max_retries' });
              }
            }
          } catch (error) {
            console.error('❌ Failed to parse CueScore RO response:', error);
          }
        };

        socket.onerror = (error) => {
          console.error('❌ WebSocket RO error:', error);
          if (!resolved) {
            resolved = true;
            clearTimeout(timeout);
            reject(error);
          }
        };

        socket.onclose = (event) => {
          console.log('🔌 WebSocket RO closed:', event.code, event.reason);
          if (!resolved) {
            resolved = true;
            clearTimeout(timeout);
            resolve({ success: false, reason: 'connection_closed' });
          }
        };
      });

    } catch (error) {
      console.error('⚠️ Run Out CueScore sync failed:', error);
    } finally {
      if (socket) {
        socket.close();
      }
      if (iframe && iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    }
  };

  // Remove the most recent Run Out from CueScore via WebSocket UNDO
  const removeCuescoreRunOut = async (player, matchId, tableScoreboardCode) => {
    if (!matchId || !tableScoreboardCode) {
      console.warn('⚠️ Missing matchId or tableScoreboardCode for RO removal - skipping CueScore sync');
      return;
    }

    let iframe = null;
    let socket = null;

    try {
      const playerLetter = player === 'player1' ? 'A' : 'B';
      console.log(`🎱 Removing Run Out from CueScore: Player ${playerLetter}, matchId: ${matchId}, code: ${tableScoreboardCode}`);

      // Step 1: Load scoreboard iframe to establish session
      await new Promise((resolve, reject) => {
        iframe = document.createElement('iframe');
        iframe.style.cssText = 'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;';
        iframe.src = `https://cuescore.com/scoreboard/?code=${tableScoreboardCode}`;

        const timeout = setTimeout(() => {
          reject(new Error('Scoreboard load timeout'));
        }, 10000);

        iframe.onload = () => {
          clearTimeout(timeout);
          console.log('✅ Scoreboard iframe loaded for RO removal');
          setTimeout(resolve, 500);
        };

        iframe.onerror = () => {
          clearTimeout(timeout);
          reject(new Error('Failed to load scoreboard'));
        };

        document.body.appendChild(iframe);
      });

      // Step 2: Connect WebSocket, get current notes, then send UNDO with expectedServerNotes
      await new Promise((resolve, reject) => {
        const wsUrl = `wss://ws.cuescore.com:8443/?matchId=${matchId}&tableCode=${tableScoreboardCode}`;
        console.log(`🔌 Connecting to WebSocket for RO removal: ${wsUrl}`);

        socket = new WebSocket(wsUrl);
        let resolved = false;

        const generateUUID = () => {
          return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
          });
        };

        const clientId = generateUUID();

        const sendUndoMessage = (serverNotes) => {
          // CueScore UNDO format requires expectedServerNotes
          const payload = {
            action: "UNDO",
            clientId: clientId,
            tableCode: tableScoreboardCode,
            matchId: parseInt(matchId, 10),
            expectedServerNotes: serverNotes
          };

          console.log('📤 Sending UNDO payload:', JSON.stringify(payload, null, 2));
          socket.send(JSON.stringify(payload));
        };

        const timeout = setTimeout(() => {
          if (!resolved) {
            resolved = true;
            console.log('⏱️ RO removal WebSocket timeout - proceeding anyway');
            resolve({ success: false, reason: 'timeout' });
          }
        }, 10000);

        socket.onopen = () => {
          console.log('✅ WebSocket connected for RO removal');
          // First, send an empty ADD to get current serverNotes via CONFLICT
          const probePayload = {
            action: "ADD",
            clientId: clientId,
            tableCode: tableScoreboardCode,
            matchId: parseInt(matchId, 10),
            expectedServerNotes: [],
            newNotes: []
          };
          setTimeout(() => {
            console.log('📤 Probing for current notes...');
            socket.send(JSON.stringify(probePayload));
          }, 500);
        };

        socket.onmessage = (event) => {
          try {
            console.log('📨 RAW CueScore RO removal response:', event.data);
            const data = JSON.parse(event.data);
            console.log('📨 PARSED CueScore RO removal response:', JSON.stringify(data, null, 2));

            if (data.action === 'CONFLICT' && data.serverNotes) {
              // Got current notes - now send UNDO with these as expectedServerNotes
              console.log(`📋 Got ${data.serverNotes.length} current notes, sending UNDO...`);
              sendUndoMessage(data.serverNotes);
            } else if (data.action === 'UNDONE' || data.action === 'OK' || data.action === 'SUCCESS') {
              if (!resolved) {
                resolved = true;
                clearTimeout(timeout);
                console.log('✅ Run Out UNDONE from CueScore successfully!');
                resolve({ success: true });
              }
            } else if (data.serverNotes && data.action !== 'CONFLICT') {
              // Check if notes count decreased (undo worked)
              console.log(`📋 Response has ${data.serverNotes.length} notes`);
              if (!resolved) {
                resolved = true;
                clearTimeout(timeout);
                console.log('✅ UNDO completed - notes updated');
                resolve({ success: true });
              }
            }
          } catch (error) {
            console.error('❌ Failed to parse CueScore RO removal response:', error);
          }
        };

        socket.onerror = (error) => {
          console.error('❌ WebSocket RO removal error:', error);
          if (!resolved) {
            resolved = true;
            clearTimeout(timeout);
            reject(error);
          }
        };

        socket.onclose = (event) => {
          console.log('🔌 WebSocket RO removal closed:', event.code, event.reason);
          if (!resolved) {
            resolved = true;
            clearTimeout(timeout);
            resolve({ success: false, reason: 'connection_closed' });
          }
        };
      });

    } catch (error) {
      console.error('⚠️ Run Out removal CueScore sync failed:', error);
    } finally {
      if (socket) {
        socket.close();
      }
      if (iframe && iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    }
  };

  // Handle score decrease - remove RO from CueScore if this frame had an RO
  const handleScoreDecrease = async (player) => {
    const playerName = player === 'player1' ? p1Name : p2Name;
    const currentFrame = player === 'player1' ? p1Frame : p2Frame;
    const runOutFrames = player === 'player1' ? p1RunOutFrames : p2RunOutFrames;
    
    console.log(`🔴🔴🔴 MINUS BUTTON CLICKED for ${player} 🔴🔴🔴`);
    console.log(`Current frame: ${currentFrame}, RO frames: [${runOutFrames.join(', ')}]`);
    
    // Check if the current frame (being removed) had an RO
    const frameHadRO = runOutFrames.includes(currentFrame);
    
    console.log(`➖ Score decrease for ${playerName}: ${currentFrame} -> ${currentFrame - 1}`);
    console.log(`📊 RO frames for ${playerName}: [${runOutFrames.join(', ')}], Frame ${currentFrame} had RO: ${frameHadRO}`);
    
    // Decrease the score
    if (player === 'player1') {
      setP1Frame(p1Frame - 1);
    } else {
      setP2Frame(p2Frame - 1);
    }
    
    // If this frame had an RO, remove it from tracking and CueScore
    if (frameHadRO) {
      console.log(`🎱🎱🎱 Frame ${currentFrame} had an RO - REMOVING FROM CUESCORE 🎱🎱🎱`);
      
      // Remove this frame from the RO tracking array
      if (player === 'player1') {
        setP1RunOutFrames(prev => prev.filter(f => f !== currentFrame));
      } else {
        setP2RunOutFrames(prev => prev.filter(f => f !== currentFrame));
      }
      
      // Sync removal to CueScore in background (only for CueScore matches)
      if (isCueScoreMatch && sessionId) {
        (async () => {
          try {
            const { ref: dbRef, get } = await import('firebase/database');
            const { database } = await import('./firebase');
            
            const sessionRef = dbRef(database, `sessions/${sessionId}`);
            const sessionSnap = await get(sessionRef);
            
            if (!sessionSnap.exists()) {
              console.warn('⚠️ Session not found for RO removal sync');
              return;
            }
            
            const sessionData = sessionSnap.val();
            const { matchId, organiserId, cuescoreMatch } = sessionData;
            
            const searchParams = new URLSearchParams(location.search);
            let tableId = searchParams.get('tableId') || sessionData.tableId;
            
            if (!tableId) {
              console.warn('⚠️ No tableId found for RO removal sync');
              return;
            }
            
            // Get scoreboard code from session (stored when match was assigned)
            let scoreboardCode = sessionData.scoreboardCode;
            
            // Fallback: If scoreboardCode not in session (old sessions), try venues lookup
            if (!scoreboardCode) {
              console.log('⚠️ No scoreboardCode in session, trying venues lookup...');
              try {
                const tableRef = dbRef(database, `organisers/${organiserId}/venues`);
                const venuesSnap = await get(tableRef);
                
                if (venuesSnap.exists()) {
                  const venues = venuesSnap.val();
                  Object.values(venues).forEach(venue => {
                    if (venue.tables) {
                      Object.entries(venue.tables).forEach(([key, table]) => {
                        if (key === tableId || table.id === tableId || table.name === tableId) {
                          scoreboardCode = table.scoreboardCode;
                        }
                      });
                    }
                  });
                }
              } catch (venueError) {
                console.log('❌ Cannot access venues (tablet not authenticated):', venueError.message);
              }
            }
            
            if (!scoreboardCode) {
              console.warn('⚠️ No scoreboard code found for RO removal sync');
              return;
            }
            
            const cuescoreMatchId = cuescoreMatch?.id || matchId;
            await removeCuescoreRunOut(player, cuescoreMatchId, scoreboardCode);
            console.log(`✅ Run Out removed from CueScore for ${playerName}`);
            
          } catch (error) {
            console.error('⚠️ Run Out removal CueScore sync failed:', error);
          }
        })();
      }
    } else {
      console.log(`ℹ️ Frame ${currentFrame} did not have an RO - no CueScore removal needed`);
    }
  };

  // Handle Run Out button click - increment score and sync to CueScore
  const handleRunOut = async (player) => {
    const playerName = player === 'player1' ? p1Name : p2Name;
    const currentFrame = player === 'player1' ? p1Frame : p2Frame;
    const newFrame = currentFrame + 1;
    
    console.log(`🎱 Run Out clicked for ${playerName}`);
    
    // Immediately increment the score AND track this frame as having an RO
    if (player === 'player1') {
      setP1Frame(newFrame);
      setP1RunOutFrames(prev => [...prev, newFrame]);
      console.log(`📊 Player 1 RO frames now includes: ${newFrame}`);
    } else {
      setP2Frame(newFrame);
      setP2RunOutFrames(prev => [...prev, newFrame]);
      console.log(`📊 Player 2 RO frames now includes: ${newFrame}`);
    }
    
    // Check if this is a winning frame - if so, defer RO sync until confirmation
    const isWinningFrame = newFrame >= matchFormat && matchFormat > 0;
    
    // Sync to CueScore in background (fire and forget)
    if (isCueScoreMatch && sessionId) {
      (async () => {
        try {
          const { ref: dbRef, get } = await import('firebase/database');
          const { database } = await import('./firebase');
          
          const sessionRef = dbRef(database, `sessions/${sessionId}`);
          const sessionSnap = await get(sessionRef);
          
          if (!sessionSnap.exists()) {
            console.warn('⚠️ Session not found for RO sync');
            return;
          }
          
          const sessionData = sessionSnap.val();
          const { matchId, organiserId, cuescoreMatch } = sessionData;
          
          // Get tableId from URL or session
          const searchParams = new URLSearchParams(location.search);
          let tableId = searchParams.get('tableId') || sessionData.tableId;
          
          if (!tableId) {
            console.warn('⚠️ No tableId found for RO sync');
            return;
          }
          
          // Get scoreboard code from session (stored when match was assigned)
          let scoreboardCode = sessionData.scoreboardCode;
          
          // Fallback: If scoreboardCode not in session (old sessions), try venues lookup
          if (!scoreboardCode) {
            console.log('⚠️ No scoreboardCode in session, trying venues lookup...');
            try {
              const tableRef = dbRef(database, `organisers/${organiserId}/venues`);
              const venuesSnap = await get(tableRef);
              
              if (venuesSnap.exists()) {
                const venues = venuesSnap.val();
                Object.values(venues).forEach(venue => {
                  if (venue.tables) {
                    Object.entries(venue.tables).forEach(([key, table]) => {
                      if (key === tableId || table.id === tableId || table.name === tableId) {
                        scoreboardCode = table.scoreboardCode;
                      }
                    });
                  }
                });
              }
            } catch (venueError) {
              console.log('❌ Cannot access venues (tablet not authenticated):', venueError.message);
            }
          }
          
          if (!scoreboardCode) {
            console.warn('⚠️ No scoreboard code found for RO sync');
            return;
          }
          
          const cuescoreMatchId = cuescoreMatch?.id || matchId;
          
          // If this is the winning frame, defer RO until confirmation
          if (isWinningFrame) {
            console.log(`🏆 Final frame RO - deferring CueScore sync until match confirmation`);
            setPendingFinalRunOut({ player, matchId: cuescoreMatchId, scoreboardCode });
            return;
          }
          
          await setCuescoreRunOut(player, cuescoreMatchId, scoreboardCode);
          console.log(`✅ Run Out synced to CueScore for ${playerName}`);
          
        } catch (error) {
          console.error('⚠️ Run Out CueScore sync failed:', error);
          // Score already incremented locally, so don't block on this
        }
      })();
    } else {
      console.log('ℹ️ Not a CueScore match - RO recorded locally only');
    }
  };

  // Handler for when players confirm the winner
  const handleWinnerConfirmed = async () => {
    if (isEndingMatch) {
      console.log('Already ending match, ignoring duplicate click');
      return;
    }

    setIsEndingMatch(true);
    
    try {
      // STEP 1: Clear tablet assignment FIRST (fastest path to return players to waiting screen)
      try {
        const { ref: dbRef, get, set } = await import('firebase/database');
        const { database } = await import('./firebase');
        
        // Get tableId from URL (tablet) or session data (organiser)
        const searchParams = new URLSearchParams(location.search);
        let tableId = searchParams.get('tableId');
        
        // If not in URL, get from session data
        if (!tableId) {
          const sessionRef = dbRef(database, `sessions/${sessionId}`);
          const sessionSnap = await get(sessionRef);
          if (sessionSnap.exists()) {
            tableId = sessionSnap.val().tableId;
            console.log('📱 Got tableId from session data:', tableId);
          }
        }
        
        if (tableId) {
          // Clear the match from this tablet
          const tabletMatchRef = dbRef(database, `tablets/${tableId}/currentMatch`);
          await set(tabletMatchRef, null);
          
          // Also set tablet status back to online
          const tabletStatusRef = dbRef(database, `tablets/${tableId}/status`);
          await set(tabletStatusRef, 'online');
          
          console.log('✅ Match cleared from tablet, tablet status set to online');
        } else {
          console.log('⚠️ No tableId found - tablet assignment not cleared');
        }
      } catch (clearError) {
        console.error('⚠️ Failed to clear tablet assignment:', clearError);
        // Don't block celebration if this fails
      }
      
      // STEP 2: Send pending final Run Out if exists (must happen BEFORE ending match)
      if (pendingFinalRunOut) {
        console.log('🎱 Sending deferred final frame Run Out to CueScore...');
        try {
          await setCuescoreRunOut(pendingFinalRunOut.player, pendingFinalRunOut.matchId, pendingFinalRunOut.scoreboardCode);
          console.log('✅ Final frame Run Out synced to CueScore');
          setPendingFinalRunOut(null);
        } catch (roError) {
          console.error('⚠️ Final frame Run Out sync failed:', roError);
          // Continue anyway - don't block match end
        }
      }
      
      // STEP 3: End match in CueScore (fire and forget - don't block)
      endCueScoreMatch(p1Frame, p2Frame).catch(err => {
        console.error('⚠️ CueScore match end failed (non-blocking):', err);
      });
      
      // STEP 4: Update bracket progression in Firebase (fire and forget)
      (async () => {
        console.log('🔄 Updating bracket progression in Firebase (background)...');
        try {
          const { ref: dbRef, get } = await import('firebase/database');
          const { database } = await import('./firebase');
          
          const sessionRef = dbRef(database, `sessions/${sessionId}`);
          const sessionSnap = await get(sessionRef);
          
          if (sessionSnap.exists()) {
            const sessionData = sessionSnap.val();
            const { organiserId, tournamentId, matchId, matchno, winnerNext, loserNext, p1Id, p2Id, p1Name: sP1Name, p2Name: sP2Name, p1Image, p2Image } = sessionData;
            
            // Determine winner and loser
            const p1Won = p1Frame > p2Frame;
            const winnerId = p1Won ? p1Id : p2Id;
            const loserId = p1Won ? p2Id : p1Id;
            const winnerName = p1Won ? sP1Name : sP2Name;
            const loserName = p1Won ? sP2Name : sP1Name;
            const winnerImage = p1Won ? p1Image : p2Image;
            const loserImage = p1Won ? p2Image : p1Image;
            
            console.log(`🏆 Winner: ${winnerName} (${winnerId}), Loser: ${loserName} (${loserId})`);
            console.log(`📍 winnerNext: ${winnerNext}, loserNext: ${loserNext}`);
            
            if (organiserId && tournamentId) {
              console.log('📡 Calling updateBracketProgression (callable)...');
              const functions = getFunctions();
              const updateBracket = httpsCallable(functions, 'updateBracketProgression');
              const result = await updateBracket({
                organiserId,
                tournamentId,
                matchId,
                matchno,
                winnerNext,
                loserNext,
                winnerId,
                loserId,
                winnerName,
                loserName,
                winnerImage,
                loserImage,
                p1Score: p1Frame,
                p2Score: p2Frame
              });
              
              if (result.data.success) {
                console.log('✅ Bracket progression updated!');
              } else {
                console.warn('⚠️ Bracket update returned unsuccessful:', result.data);
              }
            }
          }
        } catch (syncError) {
          console.error('⚠️ Failed to update bracket progression (background):', syncError);
        }
      })();
      
      // STEP 5: Show celebration immediately (don't wait for CueScore operations)
      setIsConfirming(false);
      setShowConfirm(false);
      setPage('celebration');
      setShowCelebration(true);
    } catch (error) {
      console.error('Error ending match:', error);
      alert('Failed to end match. Please try again or contact the organiser.');
      setIsConfirming(false);  // NEW: Resume syncing even on error
    } finally {
      setIsEndingMatch(false);
    }
  };

  // Handler for when players need to fix the score
  const handleFixScore = () => {
    console.log('Player chose to fix score');
    
    // Clear winner and confirmation, resume syncing
    setWinner(null);
    setShowConfirm(false);
    setIsConfirming(false);  // NEW: Resume syncing after fixing score
    
    // Player can now adjust scores
    // Winner detection will trigger again if they reach threshold
  };

  // Debounced score update effect
  useEffect(() => {
    if (!isInitializedRef.current) return;
    if (!isCueScoreMatch) return;

    // Clear any existing timeout
    if (cuescoreUpdateTimeoutRef.current) {
      clearTimeout(cuescoreUpdateTimeoutRef.current);
    }

    // Check if this is a winning score (dynamic based on matchFormat)
    const isWinningScore = p1Frame >= matchFormat || p2Frame >= matchFormat;
    
    if (isWinningScore) {
      console.log(`🏆 Winning score reached (${p1Frame}-${p2Frame}) - waiting for confirmation before sending to CueScore`);
      // Don't send to CueScore yet - wait for user confirmation
      return;
    }

    // Set new timeout (5 second debounce) - only for non-winning scores
    cuescoreUpdateTimeoutRef.current = setTimeout(() => {
      updateCueScoreScore(p1Frame, p2Frame);
    }, 5000);

    // Cleanup on unmount
    return () => {
      if (cuescoreUpdateTimeoutRef.current) {
        clearTimeout(cuescoreUpdateTimeoutRef.current);
      }
    };
  }, [p1Frame, p2Frame, matchFormat]);

  // Lag Winner Selector (shows at 0-0 automatically)
  if (showLagSelector && !lagWinnerConfirmed) {
    const handleLagWinnerSelect = async (selectedWinner) => {
      try {
        setIsSettingLagWinner(true);
        
        // Get tableId from URL first (tablet view), then fall back to session data (organiser view)
        const searchParams = new URLSearchParams(location.search);
        let tableId = searchParams.get('tableId');
        
        console.log('🎯 TableId from URL:', tableId);
        
        // Get session data for matchId and potentially tableId
        const { ref: dbRef, get } = await import('firebase/database');
        const { database } = await import('./firebase');
        
        const sessionRef = dbRef(database, `sessions/${sessionId}`);
        const sessionSnap = await get(sessionRef);
        
        if (!sessionSnap.exists()) {
          throw new Error('Session not found');
        }
        
        const sessionData = sessionSnap.val();
        console.log('📊 Session data:', sessionData);
        const { matchId, organiserId, cuescoreMatch } = sessionData;
        
        // Fall back to session's tableId if not in URL (organiser control panel)
        if (!tableId && sessionData.tableId) {
          tableId = sessionData.tableId;
          console.log('🎯 TableId from session data (organiser view):', tableId);
        }
        
        if (!tableId) {
          throw new Error('No tableId found - match may not be assigned to a table yet');
        }
        
        console.log('🔑 Extracted fields:', { matchId, organiserId, tableId });
        
        // Get scoreboard code from session (stored when match was assigned)
        let scoreboardCode = sessionData.scoreboardCode;
        
        // Fallback: If scoreboardCode not in session (old sessions), try venues lookup
        if (!scoreboardCode) {
          console.log('⚠️ No scoreboardCode in session, trying venues lookup...');
          try {
            const tableRef = dbRef(database, `organisers/${organiserId}/venues`);
            const venuesSnap = await get(tableRef);
            
            if (venuesSnap.exists()) {
              const venues = venuesSnap.val();
              console.log('🏢 Searching venues for table:', tableId);
              Object.values(venues).forEach(venue => {
                if (venue.tables) {
                  Object.entries(venue.tables).forEach(([key, table]) => {
                    console.log('🔍 Checking table:', key, table.name, table.id);
                    if (key === tableId || table.id === tableId || table.name === tableId) {
                      scoreboardCode = table.scoreboardCode;
                      console.log('✅ Found matching table with scoreboard code:', scoreboardCode);
                    }
                  });
                }
              });
            }
          } catch (venueError) {
            console.log('❌ Cannot access venues (tablet not authenticated):', venueError.message);
          }
        }
        
        if (!scoreboardCode) {
          throw new Error('Scoreboard code not found. Please unassign and reassign the match to the table.');
        }
        
        console.log(`📋 Using scoreboard code: ${scoreboardCode} for table: ${tableId}`);
        
        // Set lag winner in CueScore
        const cuescoreMatchId = cuescoreMatch?.id || matchId;
        await setCuescoreLagWinner(selectedWinner, cuescoreMatchId, scoreboardCode);
        
        // Update local state
        setLagWinner(selectedWinner);
        setLagWinnerConfirmed(true);
        setShowLagSelector(false);
        
        console.log(`✅ Lag winner set: ${selectedWinner}`);
      } catch (error) {
        console.error('❌ Failed to set lag winner:', error);
        alert(`Failed to set lag winner: ${error.message}`);
      } finally {
        setIsSettingLagWinner(false);
      }
    };

    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#0d1321', color: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ backgroundColor: '#151c2e', borderRadius: '20px', padding: 'clamp(24px, 4vh, 48px)', textAlign: 'center', maxWidth: '500px', width: '100%', border: '1px solid #2a3555', boxShadow: '0 12px 48px rgba(0, 0, 0, 0.5)', margin: '0 16px' }}>
          <img src="/icon.png" alt="Cue Clock Pro" style={{ width: 'clamp(40px, 6vh, 56px)', height: 'clamp(40px, 6vh, 56px)', borderRadius: '14px', marginBottom: 'clamp(8px, 1.5vh, 16px)' }} />
          <h2 style={{ fontSize: 'clamp(20px, 3.5vh, 28px)', fontWeight: '700', marginBottom: 'clamp(8px, 1.5vh, 16px)', fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.01em' }}>
            Select Lag Winner
          </h2>
          <p style={{ fontSize: 'clamp(14px, 2vh, 16px)', color: '#8b95a8', marginBottom: 'clamp(16px, 3vh, 32px)' }}>
            Who won the lag and will break first?
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'clamp(8px, 1.5vh, 16px)' }}>
            <button
              onClick={() => handleLagWinnerSelect('player1')}
              disabled={isSettingLagWinner}
              style={{ 
                padding: 'clamp(14px, 2.5vh, 24px) clamp(12px, 2vw, 16px)', 
                backgroundColor: isSettingLagWinner ? '#1c2640' : '#3b82f6', 
                color: 'white', 
                border: 'none', 
                borderRadius: '12px', 
                cursor: isSettingLagWinner ? 'not-allowed' : 'pointer', 
                fontSize: 'clamp(14px, 2vh, 17px)', 
                fontWeight: '600',
                fontFamily: "'Outfit', sans-serif",
                opacity: isSettingLagWinner ? 0.5 : 1,
                boxShadow: isSettingLagWinner ? 'none' : '0 4px 16px rgba(59, 130, 246, 0.3)',
                transition: 'all 0.2s ease'
              }}
            >
              {p1Name}
            </button>
            <button
              onClick={() => handleLagWinnerSelect('player2')}
              disabled={isSettingLagWinner}
              style={{ 
                padding: 'clamp(14px, 2.5vh, 24px) clamp(12px, 2vw, 16px)', 
                backgroundColor: isSettingLagWinner ? '#1c2640' : '#10b981', 
                color: 'white', 
                border: 'none', 
                borderRadius: '12px', 
                cursor: isSettingLagWinner ? 'not-allowed' : 'pointer', 
                fontSize: 'clamp(14px, 2vh, 17px)', 
                fontWeight: '600',
                fontFamily: "'Outfit', sans-serif",
                opacity: isSettingLagWinner ? 0.5 : 1,
                boxShadow: isSettingLagWinner ? 'none' : '0 4px 16px rgba(16, 185, 129, 0.3)',
                transition: 'all 0.2s ease'
              }}
            >
              {p2Name}
            </button>
          </div>
          {isSettingLagWinner && (
            <p style={{ marginTop: '16px', color: '#8b95a8', fontSize: '14px' }}>
              Sending lag winner to CueScore...
            </p>
          )}
        </div>
      </div>
    );
  }

  // Calculate current breaker based on lag winner and total frames
  // (Moved here so it's available to all return statements below)
  const totalFrames = p1Frame + p2Frame;
  const currentBreaker = lagWinner && lagWinnerConfirmed
    ? (totalFrames % 2 === 0 ? lagWinner : (lagWinner === 'player1' ? 'player2' : 'player1'))
    : null;

  // Confirmation Popup (only on control page)
  if (showConfirm && page === 'control') {
    const winnerImage = winner === 'player1' ? p1Image : p2Image;
    const winnerName = winner === 'player1' ? p1Name : p2Name;
    
    console.log(`🎯 CONFIRM DIALOG: winner=${winner}, winnerName=${winnerName}`);
    console.log(`🎯 CONFIRM DIALOG: p1Name=${p1Name}, p2Name=${p2Name}`);
    console.log(`🎯 CONFIRM DIALOG: p1Frame=${p1Frame}, p2Frame=${p2Frame}`);
    
    return (
      <div style={{ 
        minHeight: '100vh', 
        backgroundColor: 'rgba(0, 0, 0, 0.92)', 
        color: '#f0f2f5', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        padding: 'clamp(12px, 2vh, 24px)',
        fontFamily: "'DM Sans', sans-serif",
        backdropFilter: 'blur(4px)'
      }}>
        <div style={{
          backgroundColor: '#151c2e',
          borderRadius: 'clamp(14px, 2vh, 24px)',
          padding: 'clamp(20px, 4vh, 48px)',
          maxWidth: '600px',
          width: '100%',
          textAlign: 'center',
          border: '3px solid #10b981',
          boxShadow: '0 0 40px rgba(16, 185, 129, 0.15)'
        }}>
          {/* Player image with trophy overlay */}
          <div style={{
            position: 'relative',
            display: 'inline-block',
            marginBottom: 'clamp(12px, 2vh, 24px)'
          }}>
            {/* Player image */}
            {winnerImage ? (
              <img 
                src={winnerImage}
                alt={winnerName}
                style={{
                  width: 'clamp(70px, 12vh, 120px)',
                  height: 'clamp(70px, 12vh, 120px)',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '3px solid #10b981'
                }}
              />
            ) : (
              <div style={{
                width: 'clamp(70px, 12vh, 120px)',
                height: 'clamp(70px, 12vh, 120px)',
                borderRadius: '50%',
                backgroundColor: '#1c2640',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 'clamp(28px, 5vh, 48px)',
                border: '3px solid #10b981'
              }}>
                👤
              </div>
            )}
            {/* Trophy overlay */}
            <div style={{
              position: 'absolute',
              top: 'clamp(-12px, -2vh, -20px)',
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: 'clamp(40px, 7vh, 72px)',
              opacity: 0.85,
              filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.5))'
            }}>
              🏆
            </div>
          </div>
          
          <h1 style={{
            fontSize: 'clamp(22px, 4vh, 36px)',
            fontWeight: 'bold',
            marginBottom: 'clamp(8px, 1.5vh, 16px)',
            color: '#10b981'
          }}>
            {winnerName}
          </h1>
          
          <p style={{
            fontSize: 'clamp(16px, 2.5vh, 24px)',
            marginBottom: 'clamp(4px, 1vh, 8px)',
            color: '#8b95a8'
          }}>
            has reached {matchFormat} frames!
          </p>
          
          <p style={{
            fontSize: 'clamp(18px, 3vh, 28px)',
            fontWeight: 'bold',
            marginBottom: 'clamp(20px, 4vh, 48px)'
          }}>
            Did they win the match?
          </p>

          <div style={{
            display: 'flex',
            gap: 'clamp(8px, 1.5vh, 16px)',
            flexDirection: 'column'
          }}>
            <button
              onClick={async () => {
                console.log('Yes clicked! Winner:', winner);
                console.log('Setting page to celebration');
                
                // STEP 1: Clear tablet assignment FIRST (fastest path to return players to waiting screen)
                try {
                  const { ref: dbRef, get, set } = await import('firebase/database');
                  const { database } = await import('./firebase');
                  
                  const sessionRef = dbRef(database, `sessions/${sessionId}`);
                  const sessionSnap = await get(sessionRef);
                  
                  if (sessionSnap.exists()) {
                    const sessionData = sessionSnap.val();
                    const { tableId: sessionTableId } = sessionData;
                    
                    const searchParams = new URLSearchParams(location.search);
                    let tableIdToClear = searchParams.get('tableId') || sessionTableId;
                    
                    if (tableIdToClear) {
                      console.log('📱 Clearing tablet:', tableIdToClear);
                      const tabletMatchRef = dbRef(database, `tablets/${tableIdToClear}/currentMatch`);
                      await set(tabletMatchRef, null);
                      const tabletStatusRef = dbRef(database, `tablets/${tableIdToClear}/status`);
                      await set(tabletStatusRef, 'online');
                      console.log('✅ Match cleared from tablet, status reset to online (from control panel)');
                    }
                  }
                } catch (clearError) {
                  console.error('⚠️ Failed to clear tablet:', clearError);
                }
                
                // STEP 2: End match in CueScore (fire and forget)
                endCueScoreMatch(p1Frame, p2Frame).catch(err => {
                  console.error('⚠️ CueScore match end failed (non-blocking):', err);
                });
                
                // STEP 3: Re-sync tournament from CueScore in background (fire and forget)
                (async () => {
                  console.log('🔄 Re-syncing tournament from CueScore (background)...');
                  try {
                    const { ref: dbRef, get } = await import('firebase/database');
                    const { database } = await import('./firebase');
                    
                    const sessionRef = dbRef(database, `sessions/${sessionId}`);
                    const sessionSnap = await get(sessionRef);
                    
                    if (sessionSnap.exists()) {
                      const { organiserId } = sessionSnap.val();
                      
                      if (organiserId) {
                        const usernameRef = dbRef(database, `organisers/${organiserId}/cuescoreUsername`);
                        const usernameSnap = await get(usernameRef);
                        
                        if (usernameSnap.exists()) {
                          const cuescoreUsername = usernameSnap.val();
                          let idToken = null;
                          if (auth.currentUser) {
                            idToken = await auth.currentUser.getIdToken();
                          }
                          
                          console.log('📡 Calling fetchCueScoreTournaments (background)...');
                          await axios.post(
                            'https://us-central1-cue-clock-pro.cloudfunctions.net/fetchCueScoreTournaments',
                            { cuescoreUsername, userId: organiserId },
                            { headers: { ...(idToken && { 'Authorization': `Bearer ${idToken}` }), 'Content-Type': 'application/json' } }
                          );
                          console.log('✅ Tournament re-synced from CueScore!');
                        }
                      }
                    }
                  } catch (syncError) {
                    console.error('⚠️ CueScore sync failed (background):', syncError);
                  }
                })();
                
                // STEP 4: Show celebration immediately
                setIsConfirming(false);
                setShowConfirm(false);
                setPage('celebration');
                setShowCelebration(true);
              }}
              style={{
                padding: 'clamp(14px, 2.5vh, 24px) clamp(24px, 4vw, 48px)',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: 'clamp(10px, 1.5vh, 16px)',
                cursor: 'pointer',
                fontSize: 'clamp(16px, 2.5vh, 24px)',
                fontWeight: 'bold'
              }}
            >
              ✅ YES - They Won! (End Match)
            </button>
            
            <button
              onClick={() => {
                setShowConfirm(false);
                setWinner(null);
                setIsConfirming(false);
              }}
              style={{
                padding: 'clamp(14px, 2.5vh, 24px) clamp(24px, 4vw, 48px)',
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: 'clamp(10px, 1.5vh, 16px)',
                cursor: 'pointer',
                fontSize: 'clamp(16px, 2.5vh, 24px)',
                fontWeight: 'bold'
              }}
            >
              ❌ NO - Fix Score
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Celebration Screen (shows on both control and display)
  if (page === 'celebration' || showCelebration) {
    const winnerName = winner === 'player1' ? p1Name : p2Name;
    const searchParams = new URLSearchParams(location.search);
    const isTablet = searchParams.get('tablet') === 'true';
    
    return (
      <div style={{ 
        minHeight: '100vh', 
        backgroundColor: '#0a0e1a', 
        color: '#f0f2f5', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundImage: 'radial-gradient(ellipse at 50% 40%, rgba(251, 191, 36, 0.08) 0%, rgba(16, 185, 129, 0.04) 40%, transparent 70%)',
        fontFamily: "'DM Sans', sans-serif"
      }}>
        <div style={{ textAlign: 'center', padding: 'clamp(20px, 4vh, 48px)' }}>
          <div style={{ fontSize: 'clamp(48px, 10vh, 96px)', marginBottom: 'clamp(16px, 3vh, 32px)', animation: 'bounce 1s infinite' }}>
            🎉 🏆 🎉
          </div>
          <h1 style={{ 
            fontSize: 'clamp(36px, 8vh, 72px)', 
            fontWeight: '800', 
            color: '#fbbf24', 
            marginBottom: 'clamp(12px, 2vh, 24px)',
            textShadow: '0 0 30px rgba(251, 191, 36, 0.6), 0 0 60px rgba(251, 191, 36, 0.3)',
            fontFamily: "'Outfit', sans-serif",
            letterSpacing: '-0.02em'
          }}>
            {winnerName}
          </h1>
          <h2 style={{ 
            fontSize: 'clamp(24px, 5vh, 48px)', 
            fontWeight: '700', 
            color: '#10b981',
            textShadow: '0 0 20px rgba(16, 185, 129, 0.5)',
            fontFamily: "'Outfit', sans-serif",
            letterSpacing: '0.04em',
            textTransform: 'uppercase'
          }}>
            Wins the Match!
          </h2>
          
          {/* Match Stats */}
          <div style={{
            marginTop: 'clamp(20px, 3vh, 32px)',
            padding: 'clamp(16px, 2vh, 24px)',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '16px',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <div style={{ 
              fontSize: 'clamp(32px, 6vh, 56px)', 
              fontWeight: '700',
              fontFamily: "'Outfit', sans-serif",
              marginBottom: '12px'
            }}>
              {p1Frame} - {p2Frame}
            </div>
            {(p1RunOutFrames.length > 0 || p2RunOutFrames.length > 0) && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: 'clamp(24px, 4vw, 48px)',
                fontSize: 'clamp(14px, 2vh, 18px)',
                color: '#8b95a8'
              }}>
                <span>🎯 {p1Name}: {p1RunOutFrames.length} RO{p1RunOutFrames.length !== 1 ? 's' : ''}</span>
                <span>🎯 {p2Name}: {p2RunOutFrames.length} RO{p2RunOutFrames.length !== 1 ? 's' : ''}</span>
              </div>
            )}
          </div>
          
          <div style={{ fontSize: 'clamp(48px, 10vh, 96px)', marginTop: 'clamp(16px, 3vh, 32px)', animation: 'bounce 1s infinite' }}>
            🎊 🎯 🎊
          </div>
          
          {isTablet ? (
            <p style={{ fontSize: 'clamp(14px, 2vh, 18px)', color: '#8b95a8', marginTop: 'clamp(24px, 4vh, 48px)' }}>
              Returning to waiting screen in 3 seconds...
            </p>
          ) : isManualMatch ? (
            /* Manual Match: Show Home and New Match buttons */
            <div style={{ 
              display: 'flex', 
              gap: '16px', 
              marginTop: 'clamp(24px, 4vh, 48px)',
              flexWrap: 'wrap',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => navigate('/')}
                style={{
                  padding: 'clamp(12px, 2vh, 16px) clamp(20px, 4vw, 32px)',
                  backgroundColor: '#374151',
                  color: 'white',
                  border: 'none',
                  borderRadius: '14px',
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: '600',
                  fontFamily: "'DM Sans', sans-serif",
                  transition: 'all 0.2s ease'
                }}
              >
                🏠 Home
              </button>
              <button
                onClick={() => navigate('/manual-setup')}
                style={{
                  padding: 'clamp(12px, 2vh, 16px) clamp(20px, 4vw, 32px)',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '14px',
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: '600',
                  fontFamily: "'DM Sans', sans-serif",
                  boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                  transition: 'all 0.2s ease'
                }}
              >
                🎱 New Match
              </button>
            </div>
          ) : (
            /* Tournament Match: Show Back to Tournament button */
            <button
              onClick={async () => {
                setShowCelebration(false);
                // Navigate back to tournament
                try {
                  const { ref: dbRef, get } = await import('firebase/database');
                  const { database } = await import('./firebase');
                  
                  const sessionRef = dbRef(database, `sessions/${sessionId}`);
                  const sessionSnap = await get(sessionRef);
                  
                  if (sessionSnap.exists()) {
                    const sessionData = sessionSnap.val();
                    const { tournamentId } = sessionData;
                    
                    if (tournamentId) {
                      navigate(`/tournament/${tournamentId}`);
                    }
                  }
                } catch (error) {
                  console.error('Failed to navigate:', error);
                }
              }}
              style={{
                marginTop: 'clamp(24px, 4vh, 48px)',
                padding: 'clamp(12px, 2vh, 16px) clamp(20px, 4vw, 32px)',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '14px',
                cursor: 'pointer',
                fontSize: '18px',
                fontWeight: '600',
                fontFamily: "'DM Sans', sans-serif",
                boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                transition: 'all 0.2s ease'
              }}
            >
              ← Back to Tournament
            </button>
          )}
        </div>
      </div>
    );
  }

  // CONTROL PAGE
  if (page === 'control') {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#0d1321', color: '#f0f2f5', padding: '24px', fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <div>
              <h1 style={{ fontSize: '28px', fontWeight: '700', fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.01em' }}>Shot Clock Control</h1>
              <p style={{ fontSize: '13px', color: '#5a6478', marginTop: '4px' }}>
                Session: {sessionId}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  const message = 'Return to ' + (isOrganiser ? 'dashboard' : 'home') + '? Choose an option:\n\n' +
                    'OK = Save session (can resume later)\n' +
                    'Cancel = Stay on this page';
                  
                  if (window.confirm(message)) {
                    // Navigate to dashboard if organiser, otherwise home
                    navigate(isOrganiser ? '/organiser-dashboard' : '/');
                  }
                }}
                style={{ padding: '12px 24px', backgroundColor: '#1c2640', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px' }}
              >
                {isOrganiser ? '🎯 Dashboard' : '🏠 Home'}
              </button>
              <button
                onClick={() => setPage('display')}
                style={{ padding: '12px 24px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px' }}
              >
                📺 View Display
              </button>
            </div>
          </div>

          {/* Timer Controls */}
          <div style={{ backgroundColor: '#151c2e', borderRadius: '14px', padding: '24px', marginBottom: '24px', border: '1px solid #2a3555' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', fontFamily: "'Outfit', sans-serif" }}>Timer Controls</h2>
            
            {/* Timer Settings Steppers - Only for Organiser (not tablets) */}
            {isOrganiser && !new URLSearchParams(location.search).get('tablet') && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                {/* Shot Clock Duration Stepper */}
                <div style={{ backgroundColor: '#1c2640', borderRadius: '8px', padding: '12px' }}>
                  <div style={{ fontSize: '12px', color: '#8b95a8', marginBottom: '8px', textAlign: 'center' }}>Shot Clock</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                    <button
                      onClick={() => { 
                        if (duration > 15) {
                          actionPendingRef.current = true;
                          const newDuration = duration - 5;
                          setDuration(newDuration);
                          setRemaining(newDuration);
                          setIsRunning(false);
                        }
                      }}
                      style={{ padding: '8px 14px', backgroundColor: '#374151', color: 'white', border: 'none', borderRadius: '6px', cursor: duration <= 15 ? 'not-allowed' : 'pointer', fontSize: '18px', fontWeight: 'bold', opacity: duration <= 15 ? 0.5 : 1 }}
                    >
                      −
                    </button>
                    <span style={{ fontSize: '20px', fontWeight: 'bold', minWidth: '50px', textAlign: 'center' }}>{duration}s</span>
                    <button
                      onClick={() => { 
                        actionPendingRef.current = true;
                        const newDuration = duration + 5;
                        setDuration(newDuration);
                        setRemaining(newDuration);
                        setIsRunning(false);
                      }}
                      style={{ padding: '8px 14px', backgroundColor: '#374151', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' }}
                    >
                      +
                    </button>
                  </div>
                </div>
                
                {/* Extension Duration Stepper */}
                <div style={{ backgroundColor: '#1c2640', borderRadius: '8px', padding: '12px' }}>
                  <div style={{ fontSize: '12px', color: '#8b95a8', marginBottom: '8px', textAlign: 'center' }}>Extension</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                    <button
                      onClick={() => { 
                        if (extensionDuration > 5) {
                          actionPendingRef.current = true;
                          setExtensionDuration(extensionDuration - 5);
                        }
                      }}
                      style={{ padding: '8px 14px', backgroundColor: '#374151', color: 'white', border: 'none', borderRadius: '6px', cursor: extensionDuration <= 5 ? 'not-allowed' : 'pointer', fontSize: '18px', fontWeight: 'bold', opacity: extensionDuration <= 5 ? 0.5 : 1 }}
                    >
                      −
                    </button>
                    <span style={{ fontSize: '20px', fontWeight: 'bold', minWidth: '50px', textAlign: 'center' }}>{extensionDuration}s</span>
                    <button
                      onClick={() => { 
                        actionPendingRef.current = true;
                        setExtensionDuration(extensionDuration + 5);
                      }}
                      style={{ padding: '8px 14px', backgroundColor: '#374151', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' }}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '16px' }}>
              <button
                onClick={() => { actionPendingRef.current = true; setIsRunning(true); }}
                style={{ padding: '16px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' }}
              >
                ▶️ START
              </button>
              <button
                onClick={() => { actionPendingRef.current = true; setIsRunning(false); }}
                style={{ padding: '16px', backgroundColor: '#ca8a04', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' }}
              >
                ⏸️ PAUSE
              </button>
              <button
                onClick={() => { actionPendingRef.current = true; setRemaining(duration); setIsRunning(true); }}
                style={{ padding: '16px', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' }}
              >
                🔄 RESET
              </button>
              <button
                onClick={handleSwitchPlayer}
                style={{ padding: '16px', backgroundColor: '#8b5cf6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' }}
              >
                🔄 Switch Player
              </button>
            </div>

            <div style={{ marginTop: '16px', textAlign: 'center', color: '#8b95a8' }}>
              Current Time: {formatTime(remaining)}
            </div>
          </div>

          {/* Players */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
            
            {/* Player 1 */}
            <div style={{ backgroundColor: '#151c2e', borderRadius: '14px', padding: '24px', border: p1Active ? '3px solid #10b981' : '3px solid transparent', transition: 'border-color 0.3s ease' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', fontFamily: "'Outfit', sans-serif" }}>
                {isCueScoreMatch ? `👤 ${p1Name}` : '👤 Player 1'}
                {currentBreaker === 'player1' && (
                  <span style={{ fontSize: '14px', backgroundColor: '#fbbf24', color: '#0d1321', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold' }}>
                    🎱 BREAKING
                  </span>
                )}
              </h3>
              
              {p1Image && (
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <img 
                    src={p1Image} 
                    alt={p1Name}
                    style={{ 
                      width: '80px', 
                      height: '80px', 
                      borderRadius: '50%', 
                      objectFit: 'cover',
                      border: '3px solid #2a3555'
                    }}
                  />
                </div>
              )}
              
              {!isCueScoreMatch && (
                <input
                  type="text"
                  value={p1Name}
                  onChange={(e) => setP1Name(e.target.value)}
                  style={{ width: '100%', padding: '8px', backgroundColor: '#1c2640', color: 'white', border: 'none', borderRadius: '8px', marginBottom: '16px' }}
                  placeholder="Player name"
                />
              )}
              
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', color: '#8b95a8', marginBottom: '8px' }}>Frames Won</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    onClick={() => handleScoreDecrease('player1')}
                    style={{ padding: '8px 16px', backgroundColor: '#1c2640', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                  >
                    -
                  </button>
                  <span style={{ fontSize: '24px', fontWeight: 'bold', flex: 1, textAlign: 'center' }}>{p1Frame}</span>
                  <button
                    onClick={() => setP1Frame(p1Frame + 1)}
                    style={{ padding: '8px 16px', backgroundColor: '#1c2640', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                  >
                    +
                  </button>
                </div>
                {/* RO button - always visible */}
                <button
                  onClick={() => handleRunOut('player1')}
                  style={{ 
                    width: '100%',
                    marginTop: '8px',
                    padding: '8px 16px', 
                    backgroundColor: '#d97706', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '8px', 
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                  title="Run Out - Player cleared the table"
                >
                  🎱 Run Out (RO)
                </button>
              </div>

              <button
                onClick={() => handlePlayerExtension(1)}
                disabled={p1ExtensionUsed}
                style={{ 
                  width: '100%', 
                  padding: '12px', 
                  backgroundColor: p1ExtensionUsed ? '#1c2640' : '#3b82f6', 
                  color: p1ExtensionUsed ? '#5a6478' : 'white', 
                  border: 'none', 
                  borderRadius: '8px', 
                  cursor: p1ExtensionUsed ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}
              >
                ⏱️ Extension {p1ExtensionUsed ? '✓ Used' : '• Available'}
              </button>
            </div>

            {/* Player 2 */}
            <div style={{ backgroundColor: '#151c2e', borderRadius: '14px', padding: '24px', border: !p1Active ? '3px solid #10b981' : '3px solid transparent', transition: 'border-color 0.3s ease' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', fontFamily: "'Outfit', sans-serif" }}>
                {isCueScoreMatch ? `👤 ${p2Name}` : '👤 Player 2'}
                {currentBreaker === 'player2' && (
                  <span style={{ fontSize: '14px', backgroundColor: '#fbbf24', color: '#0d1321', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold' }}>
                    🎱 BREAKING
                  </span>
                )}
              </h3>
              
              {p2Image && (
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <img 
                    src={p2Image} 
                    alt={p2Name}
                    style={{ 
                      width: '80px', 
                      height: '80px', 
                      borderRadius: '50%', 
                      objectFit: 'cover',
                      border: '3px solid #2a3555'
                    }}
                  />
                </div>
              )}
              
              {!isCueScoreMatch && (
                <input
                  type="text"
                  value={p2Name}
                  onChange={(e) => setP2Name(e.target.value)}
                  style={{ width: '100%', padding: '8px', backgroundColor: '#1c2640', color: 'white', border: 'none', borderRadius: '8px', marginBottom: '16px' }}
                  placeholder="Player name"
                />
              )}
              
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', color: '#8b95a8', marginBottom: '8px' }}>Frames Won</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    onClick={() => handleScoreDecrease('player2')}
                    style={{ padding: '8px 16px', backgroundColor: '#1c2640', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                  >
                    -
                  </button>
                  <span style={{ fontSize: '24px', fontWeight: 'bold', flex: 1, textAlign: 'center' }}>{p2Frame}</span>
                  <button
                    onClick={() => setP2Frame(p2Frame + 1)}
                    style={{ padding: '8px 16px', backgroundColor: '#1c2640', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                  >
                    +
                  </button>
                </div>
                {/* RO button - always visible */}
                <button
                  onClick={() => handleRunOut('player2')}
                  style={{ 
                    width: '100%',
                    marginTop: '8px',
                    padding: '8px 16px', 
                    backgroundColor: '#d97706', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '8px', 
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                  title="Run Out - Player cleared the table"
                >
                  🎱 Run Out (RO)
                </button>
              </div>

              <button
                onClick={() => handlePlayerExtension(2)}
                disabled={p2ExtensionUsed}
                style={{ 
                  width: '100%', 
                  padding: '12px', 
                  backgroundColor: p2ExtensionUsed ? '#1c2640' : '#3b82f6', 
                  color: p2ExtensionUsed ? '#5a6478' : 'white', 
                  border: 'none', 
                  borderRadius: '8px', 
                  cursor: p2ExtensionUsed ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}
              >
                ⏱️ Extension {p2ExtensionUsed ? '✓ Used' : '• Available'}
              </button>
            </div>
          </div>

          {/* Match Info */}
          <div style={{ backgroundColor: '#1c2640', borderRadius: '14px', padding: '24px', border: '1px solid #2a3555' }}>
            <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px' }}>📋 Match Information</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', color: '#8b95a8', marginBottom: '8px' }}>First to</label>
                <input
                  type="number"
                  min="1"
                  value={matchFormat}
                  onChange={(e) => setMatchFormat(parseInt(e.target.value) || 1)}
                  style={{ width: '100%', padding: '8px', backgroundColor: '#1c2640', color: 'white', border: 'none', borderRadius: '8px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', color: '#8b95a8', marginBottom: '8px' }}>Table Name</label>
                <input
                  type="text"
                  value={tableName}
                  onChange={(e) => setTableName(e.target.value)}
                  style={{ width: '100%', padding: '8px', backgroundColor: '#1c2640', color: 'white', border: 'none', borderRadius: '8px' }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // DISPLAY PAGE
  return (
    <div style={{ height: `${viewport.h}px`, backgroundColor: '#0d1321', color: '#f0f2f5', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: "'DM Sans', sans-serif" }}>
      {/* Top Player Strip */}
      <div style={{ 
        backgroundColor: p1Active ? '#0d2818' : '#151c2e', 
        border: p1Active ? '3px solid #10b981' : '3px solid transparent',
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
        padding: !showShotClock ? 'clamp(16px, 3vh, 32px) clamp(12px, 2vw, 24px)' : (isCompact ? 'clamp(4px, 0.8vh, 8px) clamp(8px, 1.5vw, 16px)' : (deviceTier !== 'phone') ? 'clamp(16px, 3vh, 32px) clamp(24px, 4vw, 48px)' : 'clamp(8px, 1.5vh, 16px) clamp(12px, 2vw, 24px)'),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: !showShotClock ? undefined : 0,
        flex: !showShotClock ? '1 1 auto' : undefined
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: (deviceTier !== 'phone') ? 'clamp(16px, 3vw, 32px)' : 'clamp(8px, 1.5vw, 16px)', flex: 1 }}>
          {/* Player Image + BREAKING badge below in scoreboard mode */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
          {p1Image ? (
            <img 
              src={p1Image} 
              alt={p1Name}
              style={{ 
                width: !showShotClock ? ((isLandscape && deviceTier === 'phone') ? 'clamp(63px, 10vh, 125px)' : 'clamp(50px, 8vh, 100px)') : (deviceTier !== 'phone' && !isLandscape) ? 'clamp(60px, 8.63vh, 87px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(70px, 10vh, 100px)' : 'clamp(35px, 5vh, 50px)', 
                height: !showShotClock ? ((isLandscape && deviceTier === 'phone') ? 'clamp(63px, 10vh, 125px)' : 'clamp(50px, 8vh, 100px)') : (deviceTier !== 'phone' && !isLandscape) ? 'clamp(60px, 8.63vh, 87px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(70px, 10vh, 100px)' : 'clamp(35px, 5vh, 50px)', 
                borderRadius: '50%', 
                objectFit: 'cover',
                border: '2px solid #2a3555',
                flexShrink: 0
              }} 
            />
          ) : (
            <div style={{ 
              width: !showShotClock ? ((isLandscape && deviceTier === 'phone') ? 'clamp(63px, 10vh, 125px)' : 'clamp(50px, 8vh, 100px)') : (deviceTier !== 'phone' && !isLandscape) ? 'clamp(60px, 8.63vh, 87px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(70px, 10vh, 100px)' : 'clamp(35px, 5vh, 50px)', 
              height: !showShotClock ? ((isLandscape && deviceTier === 'phone') ? 'clamp(63px, 10vh, 125px)' : 'clamp(50px, 8vh, 100px)') : (deviceTier !== 'phone' && !isLandscape) ? 'clamp(60px, 8.63vh, 87px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(70px, 10vh, 100px)' : 'clamp(35px, 5vh, 50px)', 
              borderRadius: '50%', 
              backgroundColor: '#1c2640',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 'clamp(14px, 2vh, 20px)',
              flexShrink: 0,
              border: '2px solid #2a3555'
            }}>
              👤
            </div>
          )}
          {/* BREAKING badge under image in both modes */}
          {currentBreaker === 'player1' && (
            <span style={{ fontSize: 'clamp(9px, 1.2vh, 13px)', backgroundColor: '#f59e0b', color: '#0d1321', padding: '2px clamp(6px, 1vw, 10px)', borderRadius: '4px', fontWeight: '700', whiteSpace: 'nowrap', fontFamily: "'Outfit', sans-serif", letterSpacing: '0.04em', marginTop: '4px', boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)' }}>
              BREAKING
            </span>
          )}
          </div>
          
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: !showShotClock ? ((isLandscape && deviceTier === 'phone') ? 'clamp(23px, 5vh, 45px)' : 'clamp(18px, 4vh, 36px)') : (deviceTier !== 'phone' && !isLandscape) ? 'clamp(24px, 4.35vh, 42px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(28px, 5vh, 48px)' : 'clamp(14px, 2.5vh, 24px)', fontWeight: '700', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: showShotClock ? 'nowrap' : 'normal', fontFamily: "'Outfit', sans-serif" }}>
              {!showShotClock ? p1Name.split(' ').map((part, i) => <div key={i}>{part}</div>) : p1Name}
            </div>
            {showShotClock && (
            <div style={{ fontSize: (deviceTier !== 'phone') ? 'clamp(20px, 2.8vh, 28px)' : 'clamp(10px, 1.4vh, 14px)', color: '#8b95a8', marginBottom: (deviceTier !== 'phone') ? 'clamp(8px, 1vh, 16px)' : 'clamp(4px, 0.5vh, 8px)' }}>
              Extension: {p1ExtensionUsed ? '✗ Used' : '✓ Available'}
            </div>
            )}
                        {/* RO (Run Out) button - under player name */}
            <button
              onClick={() => handleRunOut('player1')}
              disabled={showConfirm}
              style={{ 
                padding: (deviceTier !== 'phone') ? 'clamp(8px, 1.2vh, 16px) clamp(32px, 5vw, 56px)' : 'clamp(4px, 0.6vh, 8px) clamp(16px, 2.5vw, 28px)', 
                backgroundColor: showConfirm ? '#1c2640' : '#d97706', 
                color: showConfirm ? '#5a6478' : 'white', 
                border: 'none', 
                borderRadius: '4px', 
                cursor: showConfirm ? 'not-allowed' : 'pointer',
                fontSize: (deviceTier !== 'phone') ? 'clamp(18px, 2.4vh, 24px)' : 'clamp(9px, 1.2vh, 12px)',
                fontWeight: 'bold',
                opacity: showConfirm ? 0.5 : 1
              }}
              title="Run Out - Player cleared the table"
            >
              🎱 RO
            </button>
          </div>
        </div>
        
        {/* View Toggle - inside top player strip for phone landscape */}
        {isLandscape && deviceTier === 'phone' && showShotClock && (
          <div 
            onClick={() => setShowShotClock(!showShotClock)}
            style={{ 
              display: 'flex', alignItems: 'center', gap: '0', cursor: 'pointer',
              backgroundColor: '#151c2e', borderRadius: '20px', padding: '2px', border: '1px solid #2a3555', flexShrink: 0, marginRight: 'clamp(8px, 1.5vw, 16px)'
            }}
          >
            <div style={{ padding: '3px 10px', borderRadius: '18px', fontSize: 'clamp(9px, 1.2vh, 12px)', fontWeight: '600', fontFamily: "'Outfit', sans-serif", backgroundColor: '#10b981', color: '#0d1321' }}>⏱️ Clock</div>
            <div style={{ padding: '3px 10px', borderRadius: '18px', fontSize: 'clamp(9px, 1.2vh, 12px)', fontWeight: '600', fontFamily: "'Outfit', sans-serif", backgroundColor: 'transparent', color: '#5a6478' }}>🏆 Score</div>
          </div>
        )}

        {/* BREAKING badge moved next to RO button above */}
        
        <div style={{ display: 'flex', alignItems: 'center', gap: !showShotClock ? 'clamp(4px, 1vw, 12px)' : 'clamp(8px, 1.5vw, 16px)', flexShrink: 0 }}>
          <div style={{ fontSize: !showShotClock ? ((isLandscape && deviceTier === 'phone') ? 'clamp(120px, 30vh, 400px)' : 'clamp(96px, 24vh, 320px)') : (deviceTier !== 'phone' && !isLandscape) ? 'clamp(83px, 17.25vh, 186px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(96px, 20vh, 216px)' : 'clamp(48px, 10vh, 108px)', fontWeight: '700', minWidth: !showShotClock ? 'auto' : '60px', textAlign: 'center', lineHeight: 1, fontFamily: "'JetBrains Mono', monospace" }}>
            {p1Frame}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(4px, 0.8vh, 8px)' }}>
            <button
              onClick={() => setP1Frame(p1Frame + 1)}
              disabled={showConfirm}
              style={{ 
                padding: !showShotClock ? ((deviceTier !== 'phone') ? 'clamp(12px, 2.4vh, 40px) clamp(16px, 3vw, 56px)' : 'clamp(6px, 1.2vh, 20px) clamp(8px, 1.5vw, 28px)') : (deviceTier !== 'phone' && !isLandscape) ? 'clamp(11px, 2.1vh, 21px) clamp(18px, 3.45vw, 35px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(12px, 2.4vh, 24px) clamp(20px, 4vw, 40px)' : 'clamp(6px, 1.2vh, 12px) clamp(10px, 2vw, 20px)', 
                backgroundColor: showConfirm ? '#1c2640' : '#10b981', 
                color: showConfirm ? '#5a6478' : 'white', 
                border: 'none', 
                borderRadius: '8px', 
                cursor: showConfirm ? 'not-allowed' : 'pointer',
                fontSize: 'clamp(14px, 2vh, 20px)',
                fontWeight: 'bold',
                opacity: showConfirm ? 0.5 : 1
              }}
            >
              +
            </button>
            <button
              onClick={() => handleScoreDecrease('player1')}
              disabled={showConfirm}
              style={{ 
                padding: !showShotClock ? ((deviceTier !== 'phone') ? 'clamp(12px, 2.4vh, 40px) clamp(16px, 3vw, 56px)' : 'clamp(6px, 1.2vh, 20px) clamp(8px, 1.5vw, 28px)') : (deviceTier !== 'phone' && !isLandscape) ? 'clamp(11px, 2.1vh, 21px) clamp(18px, 3.45vw, 35px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(12px, 2.4vh, 24px) clamp(20px, 4vw, 40px)' : 'clamp(6px, 1.2vh, 12px) clamp(10px, 2vw, 20px)', 
                backgroundColor: showConfirm ? '#1c2640' : '#dc2626', 
                color: showConfirm ? '#5a6478' : 'white', 
                border: 'none', 
                borderRadius: '8px', 
                cursor: showConfirm ? 'not-allowed' : 'pointer',
                fontSize: 'clamp(14px, 2vh, 20px)',
                fontWeight: 'bold',
                opacity: showConfirm ? 0.5 : 1
              }}
            >
              −
            </button>
          </div>
        </div>
      </div>

      {/* View Toggle - Shot Clock / Scoreboard */}
      {/* In landscape+shotclock mode on PHONE, toggle is shown inside the compact controls instead */}
      {(!showShotClock || !isLandscape || deviceTier !== 'phone') && (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        padding: '4px 0',
        backgroundColor: '#0a0e1a',
        flexShrink: 0
      }}>
        <div 
          onClick={() => setShowShotClock(!showShotClock)}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0',
            cursor: 'pointer',
            backgroundColor: '#151c2e',
            borderRadius: '20px',
            padding: '2px',
            border: '1px solid #2a3555'
          }}
        >
          <div style={{ 
            padding: '4px 12px', 
            borderRadius: '18px',
            fontSize: 'clamp(10px, 1.3vh, 13px)',
            fontWeight: '600',
            fontFamily: "'Outfit', sans-serif",
            backgroundColor: showShotClock ? '#10b981' : 'transparent',
            color: showShotClock ? '#0d1321' : '#5a6478',
            transition: 'all 0.2s ease'
          }}>
            ⏱️ Clock
          </div>
          <div style={{ 
            padding: '4px 12px', 
            borderRadius: '18px',
            fontSize: 'clamp(10px, 1.3vh, 13px)',
            fontWeight: '600',
            fontFamily: "'Outfit', sans-serif",
            backgroundColor: !showShotClock ? '#3b82f6' : 'transparent',
            color: !showShotClock ? 'white' : '#5a6478',
            transition: 'all 0.2s ease'
          }}>
            🏆 Score
          </div>
        </div>
      </div>
      )}

      {/* MASSIVE TIMER - Center Focus (Shot Clock mode only) */}
      {showShotClock && (
      <div style={{ 
        flex: '1 1 auto', 
        display: 'flex', 
        flexDirection: (isLandscape && deviceTier === 'phone') ? 'row' : 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        padding: isCompact ? 'clamp(2px, 0.5vh, 8px) clamp(12px, 2vw, 24px)' : 'clamp(4px, 1vh, 16px)',
        minHeight: 0,
        gap: (isLandscape && deviceTier === 'phone') ? 'clamp(8px, 1.5vw, 20px)' : '0'
      }}>

        {/* Phone landscape: 3-column layout [START/PAUSE | Timer+Extension | RESET/SWITCH] */}
        {isLandscape && deviceTier === 'phone' && (
          <>
          {/* Left column: START + PAUSE */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'clamp(4px, 0.8vh, 8px)', flex: '0 0 auto' }}>
            <button onClick={() => { actionPendingRef.current = true; setIsRunning(true); }} disabled={isRunning}
              style={{ padding: 'clamp(8px, 1.5vh, 14px) clamp(12px, 2vw, 24px)', backgroundColor: isRunning ? '#1c2640' : '#10b981', color: isRunning ? '#5a6478' : 'white', border: 'none', borderRadius: '6px', cursor: isRunning ? 'not-allowed' : 'pointer', fontSize: 'clamp(11px, 1.5vh, 15px)', fontWeight: 'bold', minWidth: 'clamp(80px, 12vw, 120px)' }}>
              ▶️ START
            </button>
            <button onClick={() => { actionPendingRef.current = true; setIsRunning(false); }} disabled={!isRunning}
              style={{ padding: 'clamp(8px, 1.5vh, 14px) clamp(12px, 2vw, 24px)', backgroundColor: !isRunning ? '#1c2640' : '#d97706', color: !isRunning ? '#5a6478' : 'white', border: 'none', borderRadius: '6px', cursor: !isRunning ? 'not-allowed' : 'pointer', fontSize: 'clamp(11px, 1.5vh, 15px)', fontWeight: 'bold', minWidth: 'clamp(80px, 12vw, 120px)' }}>
              ⏸️ PAUSE
            </button>
          </div>
          </>
        )}

        {/* Center: SHOT CLOCK label + Timer + Controls (tablet/large landscape controls are inside this div to stay clustered) */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: (isLandscape && deviceTier === 'phone') ? '1 1 auto' : '1 1 auto' }}>
          {!(isLandscape && deviceTier === 'phone') && (
            <div style={{ color: '#5a6478', fontSize: 'clamp(10px, 1.5vh, 18px)', marginBottom: isCompact ? '2px' : 'clamp(4px, 0.8vh, 16px)', textAlign: 'center', fontFamily: "'Outfit', sans-serif", fontWeight: '600', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
              SHOT CLOCK
            </div>
          )}
          <div style={getTimerStyle()}>
            {formatTime(remaining)}
          </div>
        
          {/* Controls below timer: portrait OR landscape on tablet/large */}
          {((!isLandscape) || (isLandscape && deviceTier !== 'phone')) && (
          <div style={{ 
            marginTop: isCompact ? 'clamp(4px, 0.8vh, 8px)' : 'clamp(8px, 2vh, 32px)',
            display: 'flex',
            flexDirection: (isLandscape && deviceTier !== 'phone') ? 'row' : 'column',
            alignItems: 'center',
            gap: isCompact ? 'clamp(4px, 0.5vh, 8px)' : 'clamp(6px, 1vh, 12px)',
            width: (isLandscape && deviceTier !== 'phone') ? '100%' : 'auto',
            justifyContent: 'center',
            maxWidth: (isLandscape && deviceTier !== 'phone') ? '700px' : '500px'
          }}>
            {/* Control buttons */}
            <div style={{ 
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: deviceTier !== 'phone' ? 'clamp(6px, 1vh, 12px)' : 'clamp(4px, 0.8vh, 10px)',
              flex: (isLandscape && deviceTier !== 'phone') ? '1 1 auto' : undefined
            }}>
            <button
              onClick={() => { actionPendingRef.current = true; setIsRunning(true); }}
              disabled={isRunning}
              style={{ 
                padding: deviceTier !== 'phone' ? ((deviceTier !== 'phone' && !isLandscape) ? 'clamp(8px, 1.58vh, 15px) clamp(14px, 1.88vw, 26px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(7px, 1.44vh, 14px) clamp(12px, 1.73vw, 23px)' : 'clamp(12px, 2.5vh, 24px) clamp(20px, 3vw, 40px)') : 'clamp(8px, 1.5vh, 14px) clamp(12px, 2vw, 24px)', 
                backgroundColor: isRunning ? '#1c2640' : '#10b981', 
                color: isRunning ? '#5a6478' : 'white', 
                border: 'none', 
                borderRadius: '8px', 
                cursor: isRunning ? 'not-allowed' : 'pointer',
                fontSize: deviceTier !== 'phone' ? ((deviceTier !== 'phone' && !isLandscape) ? 'clamp(11px, 1.58vh, 15px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(9px, 1.44vh, 14px)' : 'clamp(16px, 2.5vh, 24px)') : 'clamp(12px, 1.5vh, 16px)',
                fontWeight: 'bold',
                minWidth: deviceTier !== 'phone' ? ((deviceTier !== 'phone' && isLandscape) ? '58px' : '100px') : '80px',
                flex: '1 1 auto'
              }}
            >
              ▶️ START
            </button>
            <button
              onClick={() => { actionPendingRef.current = true; setIsRunning(false); }}
              disabled={!isRunning}
              style={{ 
                padding: deviceTier !== 'phone' ? ((deviceTier !== 'phone' && !isLandscape) ? 'clamp(8px, 1.58vh, 15px) clamp(14px, 1.88vw, 26px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(7px, 1.44vh, 14px) clamp(12px, 1.73vw, 23px)' : 'clamp(12px, 2.5vh, 24px) clamp(20px, 3vw, 40px)') : 'clamp(8px, 1.5vh, 14px) clamp(12px, 2vw, 24px)', 
                backgroundColor: !isRunning ? '#1c2640' : '#d97706', 
                color: !isRunning ? '#5a6478' : 'white', 
                border: 'none', 
                borderRadius: '8px', 
                cursor: !isRunning ? 'not-allowed' : 'pointer',
                fontSize: deviceTier !== 'phone' ? ((deviceTier !== 'phone' && !isLandscape) ? 'clamp(11px, 1.58vh, 15px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(9px, 1.44vh, 14px)' : 'clamp(16px, 2.5vh, 24px)') : 'clamp(12px, 1.5vh, 16px)',
                fontWeight: 'bold',
                minWidth: deviceTier !== 'phone' ? ((deviceTier !== 'phone' && isLandscape) ? '58px' : '100px') : '80px',
                flex: '1 1 auto'
              }}
            >
              ⏸️ PAUSE
            </button>
            <button
              onClick={() => { actionPendingRef.current = true; setRemaining(duration); setIsRunning(true); }}
              style={{ 
                padding: deviceTier !== 'phone' ? ((deviceTier !== 'phone' && !isLandscape) ? 'clamp(8px, 1.58vh, 15px) clamp(14px, 1.88vw, 26px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(7px, 1.44vh, 14px) clamp(12px, 1.73vw, 23px)' : 'clamp(12px, 2.5vh, 24px) clamp(20px, 3vw, 40px)') : 'clamp(8px, 1.5vh, 14px) clamp(12px, 2vw, 24px)', 
                backgroundColor: '#dc2626', 
                color: 'white', 
                border: 'none', 
                borderRadius: '8px', 
                cursor: 'pointer',
                fontSize: deviceTier !== 'phone' ? ((deviceTier !== 'phone' && !isLandscape) ? 'clamp(11px, 1.58vh, 15px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(9px, 1.44vh, 14px)' : 'clamp(16px, 2.5vh, 24px)') : 'clamp(12px, 1.5vh, 16px)',
                fontWeight: 'bold',
                minWidth: deviceTier !== 'phone' ? ((deviceTier !== 'phone' && isLandscape) ? '58px' : '100px') : '80px',
                flex: '1 1 auto'
              }}
            >
              🔄 RESET
            </button>
            <button
              onClick={handleSwitchPlayer}
              style={{ 
                padding: deviceTier !== 'phone' ? ((deviceTier !== 'phone' && !isLandscape) ? 'clamp(8px, 1.58vh, 15px) clamp(14px, 1.88vw, 26px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(7px, 1.44vh, 14px) clamp(12px, 1.73vw, 23px)' : 'clamp(12px, 2.5vh, 24px) clamp(20px, 3vw, 40px)') : 'clamp(8px, 1.5vh, 14px) clamp(12px, 2vw, 24px)', 
                backgroundColor: '#8b5cf6', 
                color: 'white', 
                border: 'none', 
                borderRadius: '8px', 
                cursor: 'pointer',
                fontSize: deviceTier !== 'phone' ? ((deviceTier !== 'phone' && !isLandscape) ? 'clamp(11px, 1.58vh, 15px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(9px, 1.44vh, 14px)' : 'clamp(16px, 2.5vh, 24px)') : 'clamp(12px, 1.5vh, 16px)',
                fontWeight: 'bold',
                minWidth: deviceTier !== 'phone' ? ((deviceTier !== 'phone' && isLandscape) ? '58px' : '100px') : '80px',
                flex: '1 1 auto'
              }}
            >
              🔄 SWITCH
            </button>
            </div>

            {/* Extension Button - Only for active player */}
            <div style={{ marginTop: (isLandscape && deviceTier !== 'phone') ? '0' : 'clamp(4px, 0.5vh, 8px)' }}>
              {p1Active && (
                <button
                  onClick={() => handlePlayerExtension(1)}
                  disabled={p1ExtensionUsed}
                  style={{ 
                    padding: deviceTier !== 'phone' ? ((deviceTier !== 'phone' && !isLandscape) ? 'clamp(6px, 1.28vh, 14px) clamp(11px, 1.58vw, 21px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(7px, 1.44vh, 14px) clamp(12px, 1.73vw, 23px)' : 'clamp(10px, 2vh, 20px) clamp(16px, 2.5vw, 32px)') : 'clamp(6px, 1.2vh, 14px) clamp(12px, 2vw, 28px)', 
                    backgroundColor: p1ExtensionUsed ? '#1c2640' : '#3b82f6', 
                    color: p1ExtensionUsed ? '#5a6478' : 'white', 
                    border: 'none', 
                    borderRadius: '8px', 
                    cursor: p1ExtensionUsed ? 'not-allowed' : 'pointer',
                    fontSize: deviceTier !== 'phone' ? ((deviceTier !== 'phone' && !isLandscape) ? 'clamp(9px, 1.28vh, 14px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(9px, 1.44vh, 14px)' : 'clamp(14px, 2vh, 20px)') : 'clamp(10px, 1.3vh, 14px)',
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap'
                  }}
                >
                  ⏱️ EXTENSION
                </button>
              )}
              
              {!p1Active && (
                <button
                  onClick={() => handlePlayerExtension(2)}
                  disabled={p2ExtensionUsed}
                  style={{ 
                    padding: deviceTier !== 'phone' ? ((deviceTier !== 'phone' && !isLandscape) ? 'clamp(6px, 1.28vh, 14px) clamp(11px, 1.58vw, 21px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(7px, 1.44vh, 14px) clamp(12px, 1.73vw, 23px)' : 'clamp(10px, 2vh, 20px) clamp(16px, 2.5vw, 32px)') : 'clamp(6px, 1.2vh, 14px) clamp(12px, 2vw, 28px)', 
                    backgroundColor: p2ExtensionUsed ? '#1c2640' : '#3b82f6', 
                    color: p2ExtensionUsed ? '#5a6478' : 'white', 
                    border: 'none', 
                    borderRadius: '8px', 
                    cursor: p2ExtensionUsed ? 'not-allowed' : 'pointer',
                    fontSize: deviceTier !== 'phone' ? ((deviceTier !== 'phone' && !isLandscape) ? 'clamp(9px, 1.28vh, 14px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(9px, 1.44vh, 14px)' : 'clamp(14px, 2vh, 20px)') : 'clamp(10px, 1.3vh, 14px)',
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap'
                  }}
                >
                  ⏱️ EXTENSION
                </button>
              )}
            </div>
          </div>
          )}

          {/* Phone landscape: Extension below timer in center column */}
          {isLandscape && deviceTier === 'phone' && (
            <div style={{ marginTop: 'clamp(4px, 0.8vh, 8px)' }}>
              {p1Active ? (
                <button onClick={() => handlePlayerExtension(1)} disabled={p1ExtensionUsed}
                  style={{ padding: 'clamp(6px, 1vh, 10px) clamp(12px, 2vw, 24px)', backgroundColor: p1ExtensionUsed ? '#1c2640' : '#3b82f6', color: p1ExtensionUsed ? '#5a6478' : 'white', border: 'none', borderRadius: '6px', cursor: p1ExtensionUsed ? 'not-allowed' : 'pointer', fontSize: 'clamp(10px, 1.3vh, 13px)', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                  ⏱️ EXTENSION
                </button>
              ) : (
                <button onClick={() => handlePlayerExtension(2)} disabled={p2ExtensionUsed}
                  style={{ padding: 'clamp(6px, 1vh, 10px) clamp(12px, 2vw, 24px)', backgroundColor: p2ExtensionUsed ? '#1c2640' : '#3b82f6', color: p2ExtensionUsed ? '#5a6478' : 'white', border: 'none', borderRadius: '6px', cursor: p2ExtensionUsed ? 'not-allowed' : 'pointer', fontSize: 'clamp(10px, 1.3vh, 13px)', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                  ⏱️ EXTENSION
                </button>
              )}
            </div>
          )}
        </div>

        {/* Phone landscape: Right column RESET + SWITCH */}
        {isLandscape && deviceTier === 'phone' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'clamp(4px, 0.8vh, 8px)', flex: '0 0 auto' }}>
            <button onClick={() => { actionPendingRef.current = true; setRemaining(duration); setIsRunning(true); }}
              style={{ padding: 'clamp(8px, 1.5vh, 14px) clamp(12px, 2vw, 24px)', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: 'clamp(11px, 1.5vh, 15px)', fontWeight: 'bold', minWidth: 'clamp(80px, 12vw, 120px)' }}>
              🔄 RESET
            </button>
            <button onClick={handleSwitchPlayer}
              style={{ padding: 'clamp(8px, 1.5vh, 14px) clamp(12px, 2vw, 24px)', backgroundColor: '#8b5cf6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: 'clamp(11px, 1.5vh, 15px)', fontWeight: 'bold', minWidth: 'clamp(80px, 12vw, 120px)' }}>
              🔄 SWITCH
            </button>
          </div>
        )}
      </div>
      )}

      {/* Timer Settings Bar - Only for Organiser in shot clock mode (not tablets) */}
      {showShotClock && isOrganiser && !new URLSearchParams(location.search).get('tablet') && (
        <div style={{ 
          backgroundColor: '#0a0e1a', 
          padding: 'clamp(4px, 0.8vh, 8px) clamp(8px, 1.5vw, 16px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 'clamp(16px, 3vw, 32px)',
          flexShrink: 0,
          borderTop: '1px solid #1c2640'
        }}>
          {/* Shot Clock Duration Stepper */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(4px, 0.8vw, 8px)' }}>
            <span style={{ fontSize: 'clamp(10px, 1.3vh, 12px)', color: '#8b95a8' }}>Shot Clock:</span>
            <button
              onClick={() => { 
                if (duration > 15) {
                  actionPendingRef.current = true;
                  const newDuration = duration - 5;
                  setDuration(newDuration);
                  setRemaining(newDuration);
                  setIsRunning(false);
                }
              }}
              style={{ padding: 'clamp(4px, 0.6vh, 6px) clamp(8px, 1vw, 10px)', backgroundColor: '#374151', color: 'white', border: 'none', borderRadius: '4px', cursor: duration <= 15 ? 'not-allowed' : 'pointer', fontSize: 'clamp(12px, 1.5vh, 14px)', fontWeight: 'bold', opacity: duration <= 15 ? 0.5 : 1 }}
            >
              −
            </button>
            <span style={{ fontSize: 'clamp(12px, 1.5vh, 14px)', fontWeight: 'bold', minWidth: 'clamp(30px, 4vw, 40px)', textAlign: 'center' }}>{duration}s</span>
            <button
              onClick={() => { 
                actionPendingRef.current = true;
                const newDuration = duration + 5;
                setDuration(newDuration);
                setRemaining(newDuration);
                setIsRunning(false);
              }}
              style={{ padding: 'clamp(4px, 0.6vh, 6px) clamp(8px, 1vw, 10px)', backgroundColor: '#374151', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: 'clamp(12px, 1.5vh, 14px)', fontWeight: 'bold' }}
            >
              +
            </button>
          </div>
          
          {/* Extension Duration Stepper */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(4px, 0.8vw, 8px)' }}>
            <span style={{ fontSize: 'clamp(10px, 1.3vh, 12px)', color: '#8b95a8' }}>Extension:</span>
            <button
              onClick={() => { 
                if (extensionDuration > 5) {
                  actionPendingRef.current = true;
                  setExtensionDuration(extensionDuration - 5);
                }
              }}
              style={{ padding: 'clamp(4px, 0.6vh, 6px) clamp(8px, 1vw, 10px)', backgroundColor: '#374151', color: 'white', border: 'none', borderRadius: '4px', cursor: extensionDuration <= 5 ? 'not-allowed' : 'pointer', fontSize: 'clamp(12px, 1.5vh, 14px)', fontWeight: 'bold', opacity: extensionDuration <= 5 ? 0.5 : 1 }}
            >
              −
            </button>
            <span style={{ fontSize: 'clamp(12px, 1.5vh, 14px)', fontWeight: 'bold', minWidth: 'clamp(30px, 4vw, 40px)', textAlign: 'center' }}>{extensionDuration}s</span>
            <button
              onClick={() => { 
                actionPendingRef.current = true;
                setExtensionDuration(extensionDuration + 5);
              }}
              style={{ padding: 'clamp(4px, 0.6vh, 6px) clamp(8px, 1vw, 10px)', backgroundColor: '#374151', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: 'clamp(12px, 1.5vh, 14px)', fontWeight: 'bold' }}
            >
              +
            </button>
          </div>
        </div>
      )}

      {/* Bottom Player Strip */}
      <div style={{ 
        backgroundColor: !p1Active ? '#0d2818' : '#151c2e', 
        border: !p1Active ? '3px solid #10b981' : '3px solid transparent',
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
        padding: !showShotClock ? 'clamp(16px, 3vh, 32px) clamp(12px, 2vw, 24px)' : (isCompact ? 'clamp(4px, 0.8vh, 8px) clamp(8px, 1.5vw, 16px)' : (deviceTier !== 'phone') ? 'clamp(16px, 3vh, 32px) clamp(24px, 4vw, 48px)' : 'clamp(8px, 1.5vh, 16px) clamp(12px, 2vw, 24px)'),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: !showShotClock ? undefined : 0,
        flex: !showShotClock ? '1 1 auto' : undefined
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: (deviceTier !== 'phone') ? 'clamp(16px, 3vw, 32px)' : 'clamp(8px, 1.5vw, 16px)', flex: 1 }}>
          {/* Player Image + BREAKING badge below in scoreboard mode */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
          {p2Image ? (
            <img 
              src={p2Image} 
              alt={p2Name}
              style={{ 
                width: !showShotClock ? ((isLandscape && deviceTier === 'phone') ? 'clamp(63px, 10vh, 125px)' : 'clamp(50px, 8vh, 100px)') : (deviceTier !== 'phone' && !isLandscape) ? 'clamp(60px, 8.63vh, 87px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(70px, 10vh, 100px)' : 'clamp(35px, 5vh, 50px)', 
                height: !showShotClock ? ((isLandscape && deviceTier === 'phone') ? 'clamp(63px, 10vh, 125px)' : 'clamp(50px, 8vh, 100px)') : (deviceTier !== 'phone' && !isLandscape) ? 'clamp(60px, 8.63vh, 87px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(70px, 10vh, 100px)' : 'clamp(35px, 5vh, 50px)', 
                borderRadius: '50%', 
                objectFit: 'cover',
                border: '2px solid #2a3555',
                flexShrink: 0
              }} 
            />
          ) : (
            <div style={{ 
              width: !showShotClock ? ((isLandscape && deviceTier === 'phone') ? 'clamp(63px, 10vh, 125px)' : 'clamp(50px, 8vh, 100px)') : (deviceTier !== 'phone' && !isLandscape) ? 'clamp(60px, 8.63vh, 87px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(70px, 10vh, 100px)' : 'clamp(35px, 5vh, 50px)', 
              height: !showShotClock ? ((isLandscape && deviceTier === 'phone') ? 'clamp(63px, 10vh, 125px)' : 'clamp(50px, 8vh, 100px)') : (deviceTier !== 'phone' && !isLandscape) ? 'clamp(60px, 8.63vh, 87px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(70px, 10vh, 100px)' : 'clamp(35px, 5vh, 50px)', 
              borderRadius: '50%', 
              backgroundColor: '#1c2640',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 'clamp(14px, 2vh, 20px)',
              flexShrink: 0,
              border: '2px solid #2a3555'
            }}>
              👤
            </div>
          )}
          {/* BREAKING badge under image in both modes */}
          {currentBreaker === 'player2' && (
            <span style={{ fontSize: 'clamp(9px, 1.2vh, 13px)', backgroundColor: '#f59e0b', color: '#0d1321', padding: '2px clamp(6px, 1vw, 10px)', borderRadius: '4px', fontWeight: '700', whiteSpace: 'nowrap', fontFamily: "'Outfit', sans-serif", letterSpacing: '0.04em', marginTop: '4px', boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)' }}>
              BREAKING
            </span>
          )}
          </div>
          
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: !showShotClock ? ((isLandscape && deviceTier === 'phone') ? 'clamp(23px, 5vh, 45px)' : 'clamp(18px, 4vh, 36px)') : (deviceTier !== 'phone' && !isLandscape) ? 'clamp(24px, 4.35vh, 42px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(28px, 5vh, 48px)' : 'clamp(14px, 2.5vh, 24px)', fontWeight: '700', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: showShotClock ? 'nowrap' : 'normal', fontFamily: "'Outfit', sans-serif" }}>
              {!showShotClock ? p2Name.split(' ').map((part, i) => <div key={i}>{part}</div>) : p2Name}
            </div>
            {showShotClock && (
            <div style={{ fontSize: (deviceTier !== 'phone') ? 'clamp(20px, 2.8vh, 28px)' : 'clamp(10px, 1.4vh, 14px)', color: '#8b95a8', marginBottom: (deviceTier !== 'phone') ? 'clamp(8px, 1vh, 16px)' : 'clamp(4px, 0.5vh, 8px)' }}>
              Extension: {p2ExtensionUsed ? '✗ Used' : '✓ Available'}
            </div>
            )}
                        {/* RO (Run Out) button - under player name */}
            <button
              onClick={() => handleRunOut('player2')}
              disabled={showConfirm}
              style={{ 
                padding: (deviceTier !== 'phone') ? 'clamp(8px, 1.2vh, 16px) clamp(32px, 5vw, 56px)' : 'clamp(4px, 0.6vh, 8px) clamp(16px, 2.5vw, 28px)', 
                backgroundColor: showConfirm ? '#1c2640' : '#d97706', 
                color: showConfirm ? '#5a6478' : 'white', 
                border: 'none', 
                borderRadius: '4px', 
                cursor: showConfirm ? 'not-allowed' : 'pointer',
                fontSize: (deviceTier !== 'phone') ? 'clamp(18px, 2.4vh, 24px)' : 'clamp(9px, 1.2vh, 12px)',
                fontWeight: 'bold',
                opacity: showConfirm ? 0.5 : 1
              }}
              title="Run Out - Player cleared the table"
            >
              🎱 RO
            </button>
          </div>
        </div>
        
        {/* BREAKING badge moved next to RO button above */}
        
        <div style={{ display: 'flex', alignItems: 'center', gap: !showShotClock ? 'clamp(4px, 1vw, 12px)' : 'clamp(8px, 1.5vw, 16px)', flexShrink: 0 }}>
          <div style={{ fontSize: !showShotClock ? ((isLandscape && deviceTier === 'phone') ? 'clamp(120px, 30vh, 400px)' : 'clamp(96px, 24vh, 320px)') : (deviceTier !== 'phone' && !isLandscape) ? 'clamp(83px, 17.25vh, 186px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(96px, 20vh, 216px)' : 'clamp(48px, 10vh, 108px)', fontWeight: '700', minWidth: !showShotClock ? 'auto' : '60px', textAlign: 'center', lineHeight: 1, fontFamily: "'JetBrains Mono', monospace" }}>
            {p2Frame}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(4px, 0.8vh, 8px)' }}>
            <button
              onClick={() => setP2Frame(p2Frame + 1)}
              disabled={showConfirm}
              style={{ 
                padding: !showShotClock ? ((deviceTier !== 'phone') ? 'clamp(12px, 2.4vh, 40px) clamp(16px, 3vw, 56px)' : 'clamp(6px, 1.2vh, 20px) clamp(8px, 1.5vw, 28px)') : (deviceTier !== 'phone' && !isLandscape) ? 'clamp(11px, 2.1vh, 21px) clamp(18px, 3.45vw, 35px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(12px, 2.4vh, 24px) clamp(20px, 4vw, 40px)' : 'clamp(6px, 1.2vh, 12px) clamp(10px, 2vw, 20px)', 
                backgroundColor: showConfirm ? '#1c2640' : '#10b981', 
                color: showConfirm ? '#5a6478' : 'white', 
                border: 'none', 
                borderRadius: '8px', 
                cursor: showConfirm ? 'not-allowed' : 'pointer',
                fontSize: 'clamp(14px, 2vh, 20px)',
                fontWeight: 'bold',
                opacity: showConfirm ? 0.5 : 1
              }}
            >
              +
            </button>
            <button
              onClick={() => handleScoreDecrease('player2')}
              disabled={showConfirm}
              style={{ 
                padding: !showShotClock ? ((deviceTier !== 'phone') ? 'clamp(12px, 2.4vh, 40px) clamp(16px, 3vw, 56px)' : 'clamp(6px, 1.2vh, 20px) clamp(8px, 1.5vw, 28px)') : (deviceTier !== 'phone' && !isLandscape) ? 'clamp(11px, 2.1vh, 21px) clamp(18px, 3.45vw, 35px)' : (deviceTier !== 'phone' && isLandscape) ? 'clamp(12px, 2.4vh, 24px) clamp(20px, 4vw, 40px)' : 'clamp(6px, 1.2vh, 12px) clamp(10px, 2vw, 20px)', 
                backgroundColor: showConfirm ? '#1c2640' : '#dc2626', 
                color: showConfirm ? '#5a6478' : 'white', 
                border: 'none', 
                borderRadius: '8px', 
                cursor: showConfirm ? 'not-allowed' : 'pointer',
                fontSize: 'clamp(14px, 2vh, 20px)',
                fontWeight: 'bold',
                opacity: showConfirm ? 0.5 : 1
              }}
            >
              −
            </button>
          </div>
        </div>
      </div>

      {/* Match Info Footer */}
      <div style={{ 
        textAlign: 'center', 
        padding: isCompact ? 'clamp(4px, 0.5vh, 8px)' : 'clamp(8px, 1vh, 16px)', 
        fontSize: 'clamp(12px, 1.5vh, 18px)', 
        color: '#5a6478',
        backgroundColor: '#0a0e1a',
        flexShrink: 0,
        fontFamily: "'Outfit', sans-serif",
        fontWeight: '500',
        letterSpacing: '0.04em'
      }}>
        First to {matchFormat} • {tableName}
      </div>

      {/* Winner Confirmation Overlay (for players) */}
      {showConfirm && page !== 'control' && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.92)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: 'clamp(12px, 2vh, 24px)'
        }}>
          <div style={{
            backgroundColor: '#151c2e',
            borderRadius: 'clamp(14px, 2vh, 24px)',
            padding: 'clamp(16px, 3vh, 48px)',
            maxWidth: '600px',
            width: '100%',
            textAlign: 'center',
            border: '3px solid #10b981',
            boxShadow: '0 0 40px rgba(16, 185, 129, 0.15)',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            {/* Player image with trophy overlay */}
            <div style={{
              position: 'relative',
              display: 'inline-block',
              marginBottom: 'clamp(8px, 1.5vh, 24px)'
            }}>
              {/* Player image */}
              {(winner === 'player1' ? p1Image : p2Image) ? (
                <img 
                  src={winner === 'player1' ? p1Image : p2Image}
                  alt={winner === 'player1' ? p1Name : p2Name}
                  style={{
                    width: 'clamp(60px, 10vh, 120px)',
                    height: 'clamp(60px, 10vh, 120px)',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '3px solid #10b981'
                  }}
                />
              ) : (
                <div style={{
                  width: 'clamp(60px, 10vh, 120px)',
                  height: 'clamp(60px, 10vh, 120px)',
                  borderRadius: '50%',
                  backgroundColor: '#1c2640',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'clamp(28px, 5vh, 48px)',
                  border: '3px solid #10b981'
                }}>
                  👤
                </div>
              )}
              {/* Trophy overlay */}
              <div style={{
                position: 'absolute',
                top: 'clamp(-12px, -2vh, -20px)',
                left: '50%',
                transform: 'translateX(-50%)',
                fontSize: 'clamp(40px, 7vh, 72px)',
                opacity: 0.85,
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.5))'
              }}>
                🏆
              </div>
            </div>
            
            <h1 style={{
              fontSize: 'clamp(22px, 4vh, 36px)',
              fontWeight: 'bold',
              marginBottom: '16px',
              color: '#10b981'
            }}>
              {winner === 'player1' ? p1Name : p2Name}
            </h1>
            
            <p style={{
              fontSize: 'clamp(16px, 2.5vh, 24px)',
              marginBottom: 'clamp(4px, 1vh, 8px)',
              color: '#8b95a8'
            }}>
              has reached {matchFormat} frames!
            </p>
            
            <p style={{
              fontSize: 'clamp(18px, 3vh, 28px)',
              fontWeight: 'bold',
              marginBottom: 'clamp(12px, 2.5vh, 48px)'
            }}>
              Did they win the match?
            </p>

            <div style={{
              display: 'flex',
              gap: 'clamp(8px, 1.5vh, 16px)',
              flexDirection: 'column'
            }}>
              <button
                onClick={handleWinnerConfirmed}
                disabled={isEndingMatch}
                style={{
                  padding: 'clamp(14px, 2.5vh, 24px) clamp(24px, 4vw, 48px)',
                  backgroundColor: isEndingMatch ? '#1c2640' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: 'clamp(10px, 1.5vh, 16px)',
                  cursor: isEndingMatch ? 'not-allowed' : 'pointer',
                  fontSize: 'clamp(16px, 2.5vh, 24px)',
                  fontWeight: 'bold',
                  opacity: isEndingMatch ? 0.5 : 1
                }}
              >
                {isEndingMatch ? '⏳ Ending Match...' : '✅ YES - They Won! (End Match)'}
              </button>
              
              <button
                onClick={handleFixScore}
                disabled={isEndingMatch}
                style={{
                  padding: 'clamp(14px, 2.5vh, 24px) clamp(24px, 4vw, 48px)',
                  backgroundColor: isEndingMatch ? '#1c2640' : '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: 'clamp(10px, 1.5vh, 16px)',
                  cursor: isEndingMatch ? 'not-allowed' : 'pointer',
                  fontSize: 'clamp(16px, 2.5vh, 24px)',
                  fontWeight: 'bold',
                  opacity: isEndingMatch ? 0.5 : 1
                }}
              >
                ❌ NO - Fix Score
              </button>
            </div>

            {isEndingMatch && (
              <p style={{
                marginTop: 'clamp(12px, 2vh, 24px)',
                fontSize: 'clamp(13px, 2vh, 16px)',
                color: '#8b95a8'
              }}>
                Please wait...
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/organiser-login" element={<OrganiserLogin />} />
        <Route path="/organiser-dashboard" element={<OrganiserDashboard />} />
        <Route path="/tournament/:tournamentId" element={<TournamentDetails />} />
        <Route path="/match/:sessionId" element={<MatchSession />} />
        <Route path="/tablet-login" element={<TabletLogin />} />
        <Route path="/manual-setup" element={<ManualMatchSetup />} />
        <Route path="/tablet/:tableId" element={<TabletWaiting />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/player-registration" element={<PlayerRegistration />} />
        <Route path="/registration-success" element={<RegistrationSuccess />} />
        <Route path="/adhoc-clock-select" element={<AdHocClockSelector />} />
        <Route path="/adhoc-display" element={<AdHocClockDisplay />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/player-grading" element={<PlayerGrading />} />
        <Route path="/book" element={<Book />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;